// Control UI English locale strings are the i18n source bundle.
import type { TranslationMap } from "../lib/types.ts";
import * as agentEn from "./en-agents.ts";

export const en: TranslationMap = {
  common: {
    health: "Health",
    ok: "OK",
    yes: "Yes",
    no: "No",
    active: "Active",
    loading: "Loading…",
    refreshing: "Refreshing…",
    online: "Online",
    offline: "Offline",
    connect: "Connect",
    connected: "Connected",
    refresh: "Refresh",
    retry: "Retry",
    staleData: "Showing stale data.",
    reload: "Reload",
    reset: "Reset",
    restore: "Restore",
    undo: "Undo",
    probe: "Probe",
    call: "Call",
    confirm: "Confirm",
    cancel: "Cancel",
    close: "Close",
    previous: "Previous",
    next: "Next",
    continue: "Continue",
    back: "Back",
    create: "Create",
    copy: "Copy",
    copied: "Copied!",
    copyCode: "Copy code",
    delete: "Delete",
    dismiss: "Dismiss",
    unselect: "Unselect",
    enabled: "Enabled",
    disabled: "Disabled",
    none: "none",
    na: "n/a",
    never: "never",
    configured: "Configured",
    running: "Running",
    linked: "Linked",
    mode: "Mode",
    system: "System",
    light: "Light",
    dark: "Dark",
    baseUrl: "Base URL",
    lastStart: "Last start",
    lastProbe: "Last probe",
    lastInbound: "Last inbound",
    lastConnect: "Last connect",
    lastMessage: "Last message",
    authAge: "Auth age",
    credential: "Credential",
    audience: "Audience",
    publicKey: "Public Key",
    probeOk: "Probe ok",
    probeFailed: "Probe failed",
    reloadConfig: "Reload Config",
    loadConfig: "Load config",
    loadApprovals: "Load approvals",
    settingsSections: "Settings sections",
    version: "Version",
    docs: "Docs",
    theme: "Theme",
    colorMode: "Color mode",
    colorModeOption: "Color mode: {mode}",
    resources: "Resources",
    search: "Search",
    save: "Save",
    saving: "Saving…",
    saveAndPublish: "Save & Publish",
    importing: "Importing…",
    importFromRelays: "Import from Relays",
    showAdvanced: "Show Advanced",
    hideAdvanced: "Hide Advanced",
    unsavedChanges: "You have unsaved changes",
    secondsAgo: "{count}s ago",
    working: "Working…",
    showQr: "Show QR",
    relink: "Relink",
    waitForScan: "Wait for scan",
    logout: "Logout",
    skipToMainContent: "Skip to main content",
  },
  optionCard: {
    recommended: "Recommended",
    skip: "Skip for now",
  },
  nativeLinkMenu: {
    label: "Link actions",
    openInline: "Open in Sidebar",
    openExternal: "Open in Default Browser",
    copy: "Copy Link",
  },
  filePreview: {
    label: "Support files",
    listLabel: "Files",
    searchPlaceholder: "Search files…",
    readOnly: "read-only",
    emptyTitle: "No files match",
    emptySubtitle: "Try another file name or content search.",
    copyFile: "Copy file",
    fileCount: "{count} files",
    filteredFileCount: "{count}/{total} files",
    noMatches: "No files match.",
    navigate: "navigate",
  },
  mcpApp: {
    title: "MCP App",
    unavailable: "MCP App unavailable: {error}",
  },
  githubPreview: {
    loading: "Loading GitHub details…",
    unavailable: "GitHub preview unavailable",
    states: {
      merged: "Merged",
      draft: "Draft",
      open: "Open",
      closed: "Closed",
      notPlanned: "Not planned",
    },
    file: "{count} file",
    files: "{count} files",
    comment: "{count} comment",
    comments: "{count} comments",
    pullRequest: "pull request",
    issue: "issue",
    ariaLabel: "{state} {kind} {repo} #{number}: {title}, by {author}",
  },
  channels: {
    lastError: "Last error",
    refreshingStaleSnapshot:
      "Refreshing channel status in the background; showing the last successful snapshot.",
    hub: {
      connectedTitle: "Your channels",
      addTitle: "Add a channel",
      addSubtitle: "Pick a service and follow the guided setup.",
      updatedAgo: "Updated {ago}",
      noneConnected: "No channels connected yet. Pick one below to get started.",
      partialSnapshot: "Some channel checks did not finish before the UI budget.",
      stateRunning: "Running",
      stateConfigured: "Configured",
      stateAttention: "Needs attention",
      setUp: "Set up",
      guidedSetup: "Guided step-by-step setup",
      openDetails: "Open details",
      lastMessageAgo: "Last message {ago}",
      browseAllTitle: "More channels…",
      browseAllSubtitle: "Browse every available channel, including installable plugins.",
      runSetup: "Run setup",
      saveBeforeSetup:
        "You have unsaved channel config changes. Save or reload them before running guided setup.",
    },
    setup: {
      dialogLabel: "Set up {channel}",
      title: "Set up {channel}",
      genericTitle: "a channel",
      subtitle: "A short guided setup — you can fine-tune everything later.",
      starting: "Starting setup…",
      working: "Working…",
      continue: "Continue",
      finish: "Finish",
      copyText: "Copy",
      openLink: "Open link",
      docs: "Docs",
      doneTitle: "Channel configured",
      doneBody:
        "Configuration saved. The gateway reloads the channel automatically; check its card for live status.",
      doneNoChangesTitle: "No changes made",
      doneNoChangesBody: "Setup finished without configuring a channel. Nothing was saved.",
      whatsappScanTitle: "Link WhatsApp by scanning the QR code",
      whatsappScanHelp:
        "On your phone, open WhatsApp → Settings → Linked devices → Link a device, then scan this code.",
      whatsappQrLoading: "Generating QR code…",
      whatsappQrHint: "Click Show QR to generate a pairing code.",
      whatsappLinked: "WhatsApp is linked and ready.",
      regenerateQr: "New QR code",
      linkLater: "Link later",
    },
    config: {
      schemaUnavailable: "Schema unavailable. Use Raw.",
      channelSchemaUnavailable: "Channel config schema unavailable.",
      loadingSchema: "Loading config schema…",
    },
    health: {
      title: "Channel health",
      subtitle: "Channel status snapshots from the gateway.",
      noSnapshotYet: "No snapshot yet.",
    },
    generic: {
      subtitle: "Channel status and configuration.",
    },
    discord: {
      title: "Discord",
      subtitle: "Bot status and channel configuration.",
    },
    googleChat: {
      title: "Google Chat",
      subtitle: "Chat API webhook status and channel configuration.",
    },
    imessage: {
      title: "iMessage",
      subtitle: "macOS bridge status and channel configuration.",
    },
    signal: {
      title: "Signal",
      subtitle: "signal-cli status and channel configuration.",
    },
    slack: {
      title: "Slack",
      subtitle: "Socket mode status and channel configuration.",
    },
    telegram: {
      title: "Telegram",
      subtitle: "Bot status and channel configuration.",
    },
    whatsapp: {
      title: "WhatsApp",
      subtitle: "Link WhatsApp Web and monitor connection health.",
      loggedOut: "Logged out.",
      logoutNotCleared:
        "No stored WhatsApp session was cleared. It may already be absent, or its auth directory may require manual cleanup.",
    },
    gatewayUrlConfirmation: {
      title: "Change Gateway URL",
      subtitle: "This will reconnect to a different gateway server",
      warning: "Only confirm if you trust this URL. Malicious URLs can compromise your system.",
    },
    nostr: {
      title: "Nostr",
      subtitle: "Decentralized DMs via Nostr relays (NIP-04).",
      profile: "Profile",
      editProfile: "Edit Profile",
      profilePicture: "Profile picture",
      noProfile: "No profile set.",
      noProfileHint: 'Click "Edit Profile" to add your name, bio, and avatar.',
      name: "Name",
      displayName: "Display Name",
      about: "About",
      advanced: "Advanced",
      profilePicturePreview: "Profile picture preview",
      account: "Account",
      username: "Username",
      usernameHelp: "Short username (e.g., satoshi)",
      bio: "Bio",
      bioPlaceholder: "Tell people about yourself...",
      bioHelp: "A brief bio or description",
      displayNameHelp: "Your full display name",
      avatarUrl: "Avatar URL",
      avatarHelp: "HTTPS URL to your profile picture",
      bannerUrl: "Banner URL",
      bannerHelp: "HTTPS URL to a banner image",
      website: "Website",
      websiteHelp: "Your personal website",
      nip05Identifier: "NIP-05 Identifier",
      nip05Help: "Verifiable identifier (e.g., you@domain.com)",
      lightningAddress: "Lightning Address",
      lightningHelp: "Lightning address for tips (LUD-16)",
      placeholders: {
        username: "satoshi",
        displayName: "Satoshi Nakamoto",
        avatarUrl: "https://example.com/avatar.jpg",
        bannerUrl: "https://example.com/banner.jpg",
        website: "https://example.com",
        nip05: "you@example.com",
        lightningAddress: "you@getalby.com",
      },
    },
  },
  lazyView: {
    loadingTitle: "Loading panel",
    errorTitle: "Panel failed to load",
    genericSubtitle: "Something went wrong while loading this panel.",
    staleTitle: "A new version is available",
    staleSubtitle: "OpenClaw was updated in the background. Reload to get the latest panel.",
    retry: "Retry",
  },
  nodes: {
    pairing: {
      button: "Pair mobile device",
      adminRequired: "Administrator access is required to create setup codes.",
      title: "OpenClaw mobile",
      subtitle: "Scan this QR code in the mobile app to connect a new phone.",
      noApp: "Don't have the app yet?",
      getApps: "Get the apps",
      generating: "Creating a secure setup code…",
      accessTitle: "Mobile access",
      fullAccess: "Full access (recommended)",
      fullAccessHint: "Node plus complete Gateway controls, including settings and upgrades.",
      limitedAccess: "Limited access",
      limitedAccessHint: "Node, chat, and approvals without administrative controls.",
      generateCode: "Create setup code",
      transportLimitedTitle: "Limited for network safety",
      transportLimitedHint:
        "This Gateway URL uses plaintext ws://. Use wss:// or Tailscale Serve, then create a new code for full access.",
      failed: "Could not create a setup code.",
      qrAlt: "OpenClaw mobile pairing QR code",
      qrUnavailable: "QR unavailable. Copy the setup code instead.",
      copySetupCode: "Copy setup code",
      newCode: "New code",
      showSetupCode: "Show setup code",
      pending: "Device requests waiting for review: {count}",
      review: "Review",
      waiting: "Official OpenClaw mobile apps connect automatically after scanning.",
      help: "Pairing help",
      manageDevices: "Manage devices",
    },
    binding: {
      loadConfigHint: "Load config to edit bindings.",
      formModeHint: "Switch the Config tab to Form mode to edit bindings here.",
      execNodeBinding: "Exec node binding",
      execNodeBindingSubtitle: "Pin agents to a specific node when using exec host=node.",
      defaultBinding: "Default binding",
      defaultBindingHint: "Used when agents do not override a node binding.",
      node: "Node",
      anyNode: "Any node",
      noNodes: "No nodes with system.run available.",
      noAgents: "No agents found.",
      defaultAgent: "default agent",
      agent: "agent",
      usesDefault: "uses default ({node})",
      override: "override: {node}",
      binding: "Binding",
      useDefault: "Use default",
      any: "any",
    },
    inventory: {
      title: "Paired devices",
      summaryConnected: "{connected} of {total} connected",
      summaryPending: "{count} awaiting approval",
      cleanupStale: "Clean up {count} stale",
      pendingApproval: "Pending approval",
      paired: "Paired",
      empty: "No paired devices.",
      connectedWithoutPairing: "Connected without pairing",
      olderPairing: "{count} older pairing of {name}",
      olderPairings: "{count} older pairings of {name}",
      autoPaired: "auto-paired",
      versionDrift: "version drift",
      versionDriftTitle:
        "Node {nodeVersion}; Gateway {gatewayVersion}. Update the older component to align the fleet.",
      manualWake: "manual wake required",
      manualWakeTitle:
        "The Gateway cannot wake an offline Windows node. Start the machine or restore its network connection.",
      approvalNeeded: "approval needed",
      inputAgo: "input {time} ago",
      seen: "seen {time}",
      approved: "approved {time}",
      more: "+{count} more",
      details: "Details",
      deviceId: "Device ID: {id}",
      remoteIp: "Remote IP: {ip}",
      scopes: "scopes: {scopes}",
      tokens: "Tokens",
      capabilities: "Capabilities",
      commands: "Commands",
      connected: "connected",
      offline: "offline",
      approve: "Approve",
      reject: "Reject",
      remove: "Remove",
      removeName: "Remove {name}",
      removePromptTitle: "Remove {name}?",
      removePromptBody: "This device must pair again before it can reconnect.",
      removeStalePromptTitle: "Remove {count} stale pairings?",
      removeStalePromptTitleOne: "Remove 1 stale pairing?",
      removeStalePromptBody: "Affected clients re-pair silently on their next connection.",
      gateway: "gateway",
      unpaired: "unpaired",
      unknownClient: "unknown client",
      revoked: "revoked",
      active: "active",
      rotate: "Rotate",
      revoke: "Revoke",
      none: "none",
      rolesAndScopes: "roles: {roles} · scopes: {scopes}",
      scopeUpgrade: "scope upgrade requires approval",
      roleUpgrade: "role upgrade requires approval",
      reapproval: "reconnect details changed; approval required",
      newPairing: "new device pairing request",
      repair: "repair",
      requestedAt: "{note} · requested {time}",
      requestedAccess: "requested: {access}",
      approvedAccess: "approved now: {access}",
    },
    execApprovals: {
      title: "Exec approvals",
      subtitlePrefix: "Allowlist and approval policy for",
      loadHint: "Load exec approvals to edit allowlists.",
      hostNativePolicy: "Host-native policy",
      hostNativeHint: "Read-only here. Edit from the companion app or CLI.",
      native: "Native",
      defaultAction: "Default action",
      rule: "{count} rule",
      rules: "{count} rules",
      allShells: "all shells",
      off: "off",
      on: "on",
      target: "Target",
      targetHint: "Gateway edits local approvals; node edits the selected node.",
      host: "Host",
      gateway: "Gateway",
      node: "Node",
      selectNode: "Select node",
      noNodes: "No nodes advertise exec approvals yet.",
      scope: "Scope",
      defaults: "Defaults",
      security: "Security",
      defaultSecurity: "Default security mode.",
      defaultValue: "Default: {value}.",
      mode: "Mode",
      useDefaultValue: "Use default ({value})",
      ask: "Ask",
      defaultPrompt: "Default prompt policy.",
      askFallback: "Ask fallback",
      promptUnavailable: "Applied when the UI prompt is unavailable.",
      fallback: "Fallback",
      autoAllowSkills: "Auto-allow skill CLIs",
      autoAllowSkillsHint: "Allow skill executables listed by the Gateway.",
      usingDefault: "Using default ({value}).",
      override: "Override ({value}).",
      enabled: "Enabled",
      useDefault: "Use default",
      allowlist: "Allowlist",
      allowlistHint: "Case-insensitive glob patterns.",
      addPattern: "Add pattern",
      emptyAllowlist: "No allowlist entries yet.",
      newPattern: "New pattern",
      lastUsed: "Last used: {time}",
      pattern: "Pattern",
      remove: "Remove",
      options: {
        deny: "Deny",
        allowlist: "Allowlist",
        full: "Full",
        off: "Off",
        onMiss: "On miss",
        always: "Always",
      },
    },
  },
  worktrees: {
    newWorktree: "New worktree",
    ownerManual: "Manual",
    ownerSession: "Thread",
    ownerWorkboard: "Workboard",
    title: "Managed Worktrees",
    subtitle: "Isolated repository checkouts owned by OpenClaw.",
    cleanNow: "Clean up now",
    cleanupTitle: "Cleanup",
    cleanupSubtitle:
      "OpenClaw periodically removes old worktrees to free disk space. Tune how aggressively cleanup runs.",
    cleanupMaxCount: "Max worktrees",
    cleanupMaxCountHelp:
      "Maximum number of managed worktrees to retain across all repositories. Older worktrees are snapshotted and removed first. Set to 0 to disable the count limit.",
    cleanupMaxSize: "Max total size (GB)",
    cleanupMaxSizeHelp:
      "Maximum total size in GB across all managed worktrees. Set to 0 to disable the size limit.",
    cleanupDecrease: "Decrease {label}",
    cleanupIncrease: "Increase {label}",
    cleanupSaveFailed: "Saving cleanup limits failed.",
    name: "Name",
    repo: "Repository",
    empty: "No managed worktrees.",
    restorable: "Restorable",
    restore: "Restore",
    confirmDelete: "Snapshot and delete {name}?",
    confirmForceDelete: "Snapshot failed: {error}\n\nDelete without a snapshot?",
  },
  agentChip: agentEn.agentChip,
  agentScope: agentEn.agentScope,
  newSession: {
    title: "New thread",
    hint: "Pick where this thread works, then say what to do.",
    draftRow: "New thread",
    agent: "Agent",
    where: "Where",
    gateway: "Gateway · local",
    cloudWorker: "Cloud · {profile}",
    cloudWorkerProvider: "Cloud worker provider: {provider}",
    cloudRequiresWorktree: "Cloud workers require a managed worktree",
    cloudRequiresOpenClawRuntime:
      "Cloud workers require the OpenClaw runtime; {runtime} is selected.",
    cloudSecureContextRequired:
      "Cloud workers need a secure browser context so recovery can protect your task.",
    cloudStartFailed: "The thread was created locally, but cloud startup failed: {error}",
    cloudSyncsFolder: "Syncs {folder} to the cloud worker",
    folder: "Folder",
    folderPlaceholder: "Agent workspace",
    browse: "Browse folders",
    browseRequiresAdmin: "Folder browsing needs an admin connection",
    browserUp: "Parent folder",
    browserUse: "Use this folder",
    browserEmpty: "No subfolders",
    browserLoadFailed: "Couldn't list that folder.",
    nodeOffline: "Device is offline",
    nodeCannotBrowse: "This device doesn't support folder browsing",
    hiddenFolder: "Hidden folder",
    worktree: "Worktree",
    worktreeUnavailable: "Agent workspace is not a git checkout",
    baseBranch: "Base branch",
    worktreeName: "Worktree name",
    worktreeNamePlaceholder: "auto",
    worktreeNameInvalid: "Worktree names use lowercase letters, digits, and dashes.",
    messagePlaceholder: "What should this thread work on?",
    readingAttachment: "Reading attachment",
    start: "Start thread",
    starting: "Starting…",
    createFailed: "Couldn't create the thread.",
    createOutcomeUnknown:
      "The Gateway changed while this thread was starting. Check recent threads before starting this task again.",
    catalogUnavailable: "This thread target is unavailable.",
  },
  sessionsView: {
    deletePreservedWorktrees:
      "{count} thread worktree(s) with uncommitted or unpushed work were kept ({branches}). Manage them under Settings -> Worktrees.",
    deletePreservedWorktreeConfirm:
      "The thread's worktree has uncommitted or unpushed work, so it was kept ({branch}). Delete the checkout anyway?",
    title: "Threads",
    subtitle: "Active threads and per-thread overrides.",
    store: "Store: {path}",
    active: "Updated within",
    limit: "Limit",
    filters: "Filters",
    filterControls: "Thread filters",
    sourceFilters: "Thread source filters",
    global: "Global",
    unknown: "Unknown",
    sessionState: "Thread state",
    viewArchived: "View archived",
    sessionArchived: "Thread archived",
    sessionsArchived: "Archived {count} threads",
    deleteAllArchived: "Delete all archived…",
    deleteAllArchivedConfirm: "Delete {count} archived threads and their transcripts?",
    activeTooltip: "Loads threads updated in the last {count} minutes.",
    limitTooltip: "Max threads to load.",
    globalTooltip: "Include global threads.",
    unknownTooltip: "Include unknown threads.",
    archivedOnlyTooltip: "Show only archived threads.",
    minutesPlaceholder: "min",
    searchPlaceholder: "Filter by key, agent, label, kind…",
    transcriptSearchTitle: "Search transcripts",
    transcriptSearchDescription:
      "Find exact words or phrases in user and assistant messages across the default agent's threads.",
    transcriptSearchInputLabel: "Search thread transcripts",
    transcriptSearchPlaceholder: "Search exact words or phrases…",
    transcriptSearchAction: "Search",
    transcriptSearchClear: "Clear",
    transcriptSearchRetry: "Retry",
    transcriptSearchSearching: "Searching transcripts…",
    transcriptSearchUnavailable: "Transcript search requires a newer Gateway.",
    transcriptSearchError: "Transcript search failed",
    transcriptSearchIndexing:
      "The transcript index is still updating. Retry to include recent messages.",
    transcriptSearchEmpty: "No transcript messages match that search.",
    transcriptSearchMatches: "Transcript matches: {count}",
    transcriptSearchTruncated: "Showing the first 25 matches.",
    user: "User",
    assistant: "Assistant",
    selected: "{count} selected",
    deleteSelected: "Delete",
    selectAllOnPage: "Select all on page",
    selectSession: "Select thread",
    optionalPlaceholder: "(optional)",
    key: "Key",
    label: "Label",
    kind: "Kind",
    updated: "Updated",
    tokens: "Tokens",
    compaction: "Compaction",
    goal: "Goal",
    goalNote: "Goal note",
    thinking: "Thinking",
    fast: "Fast",
    verbose: "Verbose",
    reasoning: "Reasoning",
    actions: "Actions",
    addToWorkboard: "Add to Workboard",
    openWorkboardCard: "Open Workboard card",
    dashboardAvailable: "Dashboard available",
    approvalNeeded: "Approval needed",
    noSessions: "No threads found.",
    noSessionsMatchFilters: "No threads match your filters.",
    pagination: "{start}-{end} of {total} rows",
    rowsPerPage: "{count} per page",
    showAll: "Show all",
    inherit: "inherit",
    defaultOption: "Default ({value})",
    offExplicit: "off (explicit)",
    auto: "auto",
    on: "on",
    off: "off",
    full: "full",
    stream: "stream",
    customOption: "{value} (custom)",
    manual: "manual",
    autoThreshold: "auto-threshold",
    overflowRetry: "overflow retry",
    timeoutRetry: "timeout retry",
    tokenRange: "{before} to {after} tokens",
    tokensBefore: "{count} tokens before",
    tokenDeltaUnavailable: "token delta unavailable",
    contextUsage: "{percent}% of context used ({used} / {context} tokens)",
    contextUsageApprox: "~{percent}% of context used ({used} / {context} tokens, approximate)",
    checkpoints: "{count} Checkpoints",
    checkpoint: "{count} Checkpoint",
    showSessionDetails: "Show thread details for {count}",
    hideSessionDetails: "Hide thread details for {count}",
    sessionDetails: "Thread details",
    overrides: "Overrides",
    compactionHistory: "Compaction history",
    status: "Status",
    statusLive: "Live",
    statusIdle: "Idle",
    statusUnknown: "Unknown",
    statusRunning: "Running",
    statusDone: "Done",
    statusFailed: "Failed",
    statusKilled: "Killed",
    statusTimeout: "Timed out",
    waitingForAnswer: "Waiting for your answer",
    waitingForApproval: "Waiting for approval",
    runFailedReason: "Run failed: {reason}",
    runErrorTimedOut: "Timed out",
    runErrorUnknown: "Unknown error",
    attentionRequired: "Session needs attention",
    model: "Model",
    provider: "Provider",
    runtime: "Runtime",
    runDuration: "Run duration",
    lastActive: "Last active {time}",
    surface: "Surface",
    subject: "Subject",
    room: "Room",
    space: "Space",
    sessionId: "Session ID",
    activeRun: "Active run",
    showChildSessions: "Show {count} child threads for {session}",
    hideChildSessions: "Hide {count} child threads for {session}",
    childSessions: "Child threads",
    showMoreChildren: "Show {count} more",
    archived: "Archived",
    pinned: "Pinned",
    unread: "Unread",
    worktreeSession: "Worktree thread",
    automationAttached: "Automation attached",
    cloudWorkerPlacement: "Cloud worker: {state}",
    renameSession: "Rename thread",
    renameSessionPrompt: "Rename thread",
    renameSessionMenu: "Rename…",
    pinSession: "Pin thread",
    unpinSession: "Unpin thread",
    changeIcon: "Change icon",
    customEmoji: "Custom emoji",
    invalidEmojiIcon: "Enter exactly one emoji.",
    removeIcon: "Remove icon",
    markUnread: "Mark as unread",
    markRead: "Mark as read",
    markUnreadCount: "Mark {count} as unread",
    markReadCount: "Mark {count} as read",
    forkSession: "Fork",
    openChat: "Open chat",
    openPullRequest: "Open PR",
    openInEditorMenu: "Open in",
    archiveSession: "Archive thread",
    archiveSessionCount: "Archive {count}",
    restoreSession: "Restore thread",
    stopCloudWorker: "Stop cloud worker…",
    stopCloudWorkerConfirm: 'Stop the cloud worker for "{session}"?',
    deleteSessionMenu: "Delete…",
    deleteSessionCount: "Delete {count}…",
    deleteSessionConfirm: 'Delete "{session}" and its transcript?',
    deleteSessionsConfirm: "Delete {count} threads and their transcripts?",
    groupBy: "Group by",
    groupByNone: "None",
    groupByCategory: "Custom groups",
    showCronSessions: "Show cron sessions",
    groupByChannel: "Channel",
    groupByKind: "Kind",
    groupByAgent: "Agent",
    groupByDate: "Date",
    group: "Group",
    ungrouped: "Ungrouped",
    newGroup: "New group…",
    newGroupPrompt: "New group name",
    moveToGroup: "Move thread to a group",
    moveToGroupMenu: "Move to group",
    moveToGroupMenuCount: "Move {count} to group",
    removeFromGroup: "Remove from group",
    groupMenu: "Group options for {group}",
    renameGroupMenu: "Rename group…",
    renameGroupPrompt: "Rename group",
    deleteGroupMenu: "Delete group…",
    deleteGroupConfirm: 'Delete group "{group}"? Its threads move to Ungrouped.',
    dragSessionHint: "Drag thread to move between groups",
    dateToday: "Today",
    dateYesterday: "Yesterday",
    dateThisWeek: "This week",
    dateOlder: "Older",
    dateNoActivity: "No activity",
    groupRowCount: "{count} threads",
    groupRowCountOne: "{count} thread",
    loadingCheckpoints: "Loading checkpoints…",
    noCheckpoints: "No compaction checkpoints recorded for this thread.",
    noSummary: "No summary captured.",
    branchFromCheckpoint: "Branch from checkpoint",
    restoreCheckpoint: "Restore checkpoint",
  },
  agents: {
    noAgents: "No agents",
    copyId: "Copy ID",
    copyIdTitle: "Copy agent ID to clipboard",
    default: "Default",
    setDefault: "Set Default",
    alreadyDefaultTitle: "Already the default agent",
    setDefaultTitle: "Set as the default agent",
    ...agentEn.pinning,
    selectTitle: "Select an agent",
    selectSubtitle: "Pick an agent to inspect its workspace and tools.",
    tabs: {
      overview: "Overview",
      files: "Files",
      tools: "Tools",
      skills: "Skills",
      channels: "Channels",
      cronJobs: "Automations",
      memory: "Memory",
    },
    context: {
      title: "Agent Context",
      workspace: "Workspace",
      openFilesTab: "Open Files tab",
      primaryModel: "Primary Model",
      runtime: "Runtime",
      thinkingDefault: "Thinking Default",
      identityName: "Identity Name",
      identityAvatar: "Identity Avatar",
      skillsFilter: "Skills Filter",
      default: "Default",
      configurationSubtitle: "Workspace, identity, and model configuration.",
      schedulingSubtitle: "Workspace and scheduling targets.",
    },
    identity: agentEn.identity,
    overview: {
      title: "Overview",
      subtitle: "Workspace paths and identity metadata.",
      selectedSkills: "{count} selected",
      allSkills: "all skills",
      unsavedConfig: "You have unsaved config changes.",
      modelSelection: "Model Selection",
      primaryModel: "Primary model",
      primaryModelDefault: "Primary model (default)",
      notSet: "Not set",
      inheritDefault: "Inherit default",
      inheritDefaultModel: "Inherit default ({model})",
      fallbacks: "Fallbacks",
    },
    toolCatalog: {
      groups: {
        files: "Files",
        runtime: "Runtime",
        web: "Web",
        memory: "Memory",
        sessions: "Threads",
        ui: "UI",
        messaging: "Messaging",
        automation: "Automation",
        nodes: "Nodes",
        agents: "Agents",
        media: "Media",
      },
      descriptions: {
        read: "Read file contents",
        write: "Create or overwrite files",
        edit: "Make precise edits",
        applyPatch: "Patch files (OpenAI)",
        exec: "Run shell commands",
        process: "Manage background processes",
        webSearch: "Search the web",
        webFetch: "Fetch web content",
        memorySearch: "Semantic search",
        memoryGet: "Read memory files",
        sessionsList: "List sessions",
        sessionsHistory: "Session history",
        sessionsSend: "Send to session",
        sessionsSpawn: "Spawn sub-agent",
        sessionStatus: "Session status",
        browser: "Control web browser",
        canvas: "Control canvases",
        message: "Send messages",
        cron: "Schedule tasks",
        gateway: "Gateway control",
        nodes: "Nodes + devices",
        agentsList: "List agents",
        image: "Image understanding",
      },
      profiles: {
        minimal: "Minimal",
        coding: "Coding",
        messaging: "Messaging",
        full: "Full",
      },
    },
    skillsPanel: {
      title: "Skills",
      subtitle: "Per-agent skill allowlist and workspace skills.",
      loadConfig: "Load the gateway config to set per-agent skills.",
      customAllowlist: "This agent uses a custom skill allowlist.",
      allEnabled: "All skills are enabled. Disabling any skill will create a per-agent allowlist.",
      loadAgent: "Load skills for this agent to view workspace-specific entries.",
      filter: "Filter",
      searchPlaceholder: "Search skills",
      shown: "{count} shown",
      empty: "No skills found.",
      missing: "Missing: {items}",
      reason: "Reason: {items}",
    },
    channels: {
      title: "Channels",
      subtitle: "Gateway-wide channel status snapshot.",
      lastRefresh: "Last refresh: {time}",
      loadHint: "Load channels to see live status.",
      empty: "No channels found.",
      connectedCount: "{connected}/{total} connected",
      noAccounts: "no accounts",
      configuredCount: "{count} configured",
      notConfigured: "not configured",
      enabledCount: "{count} enabled",
      setupGuide: "Setup guide",
    },
    cronPanel: {
      schedulerTitle: "Scheduler",
      schedulerSubtitle: "Gateway cron status.",
      jobs: "Jobs",
      nextWake: "Next wake",
      agentJobsTitle: "Agent Cron Jobs",
      agentJobsSubtitle: "Scheduled jobs targeting this agent.",
      noJobs: "No jobs assigned.",
      runNow: "Run Now",
    },
    files: {
      emptyDraft: "Empty draft",
      minRead: "{count} min read",
      markdownPreview: "Markdown Preview",
      extensionPreview: "{ext} Preview",
      preview: "Preview",
      expandPreview: "Expand preview",
      collapsePreview: "Collapse preview",
      editFile: "Edit file",
      closePreview: "Close preview",
      coreFilesTitle: "Core Files",
      coreFilesSubtitle: "Bootstrap persona, identity, and tool guidance.",
      workspace: "Workspace",
      loadHint: "Load the agent workspace files to edit core instructions.",
      empty: "No files found.",
      missing: "missing",
      selectFile: "Select a file to edit.",
      previewMarkdownTitle: "Preview rendered markdown",
      willCreateOnSave: "Will Create on Save",
      liveDraftPreview: "Live Draft Preview",
      savedPreview: "Saved Preview",
      updated: "Updated {time}",
      notCreatedYet: "Not Created Yet",
      updatedUnknown: "Updated Unknown",
      missingHint: "This file is missing. Saving will create it in the agent workspace.",
      content: "Content",
      words: "{count} words",
      lines: "lines",
    },
  },
  debug: {
    snapshotsTitle: "Snapshots",
    snapshotsSubtitle: "Status, health, and heartbeat data.",
    status: "Status",
    health: "Health",
    lastHeartbeat: "Last heartbeat",
    security: {
      audit: "Security audit",
      critical: "{count} critical",
      warnings: "{count} warnings",
      noCriticalIssues: "No critical issues",
      info: "{count} info",
      runPrefix: "Run",
      runSuffix: "for details.",
    },
    manualRpcTitle: "Manual RPC",
    manualRpcSubtitle: "Send a raw gateway method with JSON params.",
    callFailed: "Call failed",
    method: "Method",
    selectMethod: "Select a method…",
    paramsJson: "Params (JSON)",
    modelsTitle: "Models",
    modelsSubtitle: "Catalog from models.list.",
    eventLogTitle: "Event Log",
    eventLogSubtitle: "Latest gateway events.",
    noEvents: "No events yet.",
  },
  configForm: {
    hideValue: "Hide value",
    revealValue: "Reveal value",
    disableStreamToReveal: "Disable stream mode to reveal value",
    unsupportedType: "Unsupported type: {type}. Use Raw mode.",
    structuredSecretRaw: "Structured value (SecretRef) - use Raw mode to edit",
    structuredSecretFile: "Structured value (SecretRef) - edit the config file directly",
    defaultValue: "Default: {value}",
    resetToDefault: "Reset to default",
    select: "Select...",
    jsonValue: "JSON value",
    unsupportedArray: "Unsupported array schema. Use Raw mode.",
    itemCountOne: "{count} item",
    itemCount: "{count} items",
    add: "Add",
    noItems: 'No items yet. Click "Add" to create one.',
    removeItem: "Remove item",
    customEntries: "Custom entries",
    addEntry: "Add Entry",
    noCustomEntries: "No custom entries.",
    key: "Key",
    removeEntry: "Remove entry",
    schemaUnavailable: "Schema unavailable.",
    unsupportedSchema: "Unsupported schema. Use Raw.",
    unsupportedNode: "Unsupported schema node. Use Raw mode.",
    noSettingsMatch: 'No settings match "{query}"',
    noSettingsInSection: "No settings in this section",
    sections: {
      env: {
        label: "Environment Variables",
        description: "Environment variables passed to the gateway process",
      },
      update: {
        label: "Updates",
        description: "Auto-update settings and release channel",
      },
      agents: {
        label: "Agents",
        description: "Agent configurations, models, and identities",
      },
      auth: {
        label: "Authentication",
        description: "API keys and authentication profiles",
      },
      channels: {
        label: "Channels",
        description: "Messaging channels (Telegram, Discord, Slack, etc.)",
      },
      messages: {
        label: "Messages",
        description: "Message handling and routing settings",
      },
      commands: {
        label: "Commands",
        description: "Custom slash commands",
      },
      hooks: {
        label: "Hooks",
        description: "Webhooks and event hooks",
      },
      skills: {
        label: "Skills",
        description: "Skill packs and capabilities",
      },
      tools: {
        label: "Tools",
        description: "Tool configurations (browser, search, etc.)",
      },
      gateway: {
        label: "Gateway",
        description: "Gateway server settings (port, auth, binding)",
      },
      wizard: {
        label: "Setup Wizard",
        description: "Setup wizard state and history",
      },
      meta: {
        label: "Metadata",
        description: "Gateway metadata and version information",
      },
      logging: {
        label: "Logging",
        description: "Log levels and output configuration",
      },
      browser: {
        label: "Browser",
        description: "Browser automation settings",
      },
      ui: {
        label: "UI",
        description: "User interface preferences",
      },
      models: {
        label: "Models",
        description: "AI model configurations and providers",
      },
      bindings: {
        label: "Bindings",
        description: "Key bindings and shortcuts",
      },
      broadcast: {
        label: "Broadcast",
        description: "Broadcast and notification settings",
      },
      audio: {
        label: "Audio",
        description: "Audio input/output settings",
      },
      session: {
        label: "Session",
        description: "Session management and persistence",
      },
      cron: {
        label: "Cron",
        description: "Scheduled tasks and automation",
      },
      web: {
        label: "Web",
        description: "Web server and API settings",
      },
      discovery: {
        label: "Discovery",
        description: "Service discovery and networking",
      },
      canvasHost: {
        label: "Canvas Host",
        description: "Canvas rendering and display",
      },
      talk: {
        label: "Talk",
        description: "Voice and speech settings",
      },
      plugins: {
        label: "Plugins",
        description: "Plugin management and extensions",
      },
      diagnostics: {
        label: "Diagnostics",
        description: "Instrumentation, OpenTelemetry, and cache-trace settings",
      },
      cli: {
        label: "CLI",
        description: "CLI banner and startup behavior",
      },
      secrets: {
        label: "Secrets",
        description: "Secret provider configuration",
      },
      acp: {
        label: "ACP",
        description: "Agent Communication Protocol runtime and streaming settings",
      },
      mcp: {
        label: "MCP",
        description: "Model Context Protocol server definitions",
      },
    },
  },
  // Historical namespace from the pre-restructure "quick settings" page; its
  // keys now serve General, Privacy & Security, Appearance, and Profile.
  // Renaming would force retranslation of every key, so the name stays.
  quickSettings: {
    language: "Language",
    model: {
      title: "Model & Thinking",
      model: "Model",
      thinking: "Thinking",
      fastMode: "Fast mode",
      thinkingLevels: {
        off: "Off",
        low: "Low",
        medium: "Medium",
        high: "High",
      },
      fastModes: {
        auto: "Auto",
        fast: "Fast",
        standard: "Standard",
      },
    },
    channels: {
      title: "Channels",
      connect: "Connect →",
    },
    appearance: {
      lobsterVisits: "Lobster visits",
      lobsterVisitsOn: "Drops by occasionally",
      lobsterVisitsOff: "Never visits",
      lobsterSounds: "Lobster sounds",
      lobsterSoundsOn: "Tiny blubs when touched",
      lobsterSoundsOff: "Silent",
      lobsterdex: "Lobsterdex",
      lobsterdexSeen: "{seen}/{total} visited",
      lobsterdexFirstVisited: "{name} · first visited {date}",
    },
    security: {
      title: "Security",
      gatewayAuth: "Gateway auth",
      execPolicy: "Exec policy",
      deviceAuth: "Device auth",
      browserEnabled: "Browser enabled",
      toolProfile: "Tool profile",
    },
    system: {
      gatewayHost: "Gateway Host",
      up: "Up {duration}",
      cpu: "CPU",
      memory: "Memory",
      disk: "Disk",
      usage: "{label} usage",
      loadAverage: "Load average: {values}",
      core: "{count} core",
      cores: "{count} cores",
      load: "load",
      used: "used",
      freeOf: "{free} free of {total}",
      runtime: "Node {version} · PID {pid}",
    },
    personal: {
      title: "Personal",
      profileTitle: "Profile",
      user: "User",
      you: "You",
      assistant: "Assistant",
      localIdentity: "Your local chat identity",
      profileIdentity: "Your gateway profile",
      assistantIdentity: "Assistant identity",
      displayName: "Display name",
      displayNamePlaceholder: "Your name",
      saveProfile: "Save name",
      saving: "Saving…",
      uploading: "Processing…",
      profileHint: "JPEG, PNG, or WebP. Images are center-cropped and resized to 512px.",
      profileLoadFailed: "Could not load your profile.",
      profileSaveFailed: "Could not save your profile.",
      avatarInvalid: "Choose a valid JPEG, PNG, or WebP image.",
      avatarTooLarge: "The processed avatar is still too large.",
      avatarText: "Avatar text / emoji",
      avatarPlaceholder: "JD or 🦞",
      chooseImage: "Choose image",
      clearAvatar: "Clear avatar",
      browserOnly: "Stored in this browser only.",
      configuredAvatar: "Configured avatar",
      fallbackAvatar: "Fallback avatar",
      fallbackLogo: "Fallback logo",
      avatarIssues: {
        remoteBlocked: "Remote URLs are blocked by Control UI image policy",
        missing: "File not found",
        unsupported: "Unsupported image type",
        outsideWorkspace: "Outside workspace",
        tooLarge: "Image is too large",
        cannotRender: "Cannot render avatar",
      },
    },
  },
  configPage: {
    content: "Settings content",
    themeImported: "Imported {name}.",
    themeRemoved: "Custom theme removed.",
  },
  configView: {
    categories: {
      core: "Core",
      ai: "Agent Defaults",
      communication: "Communication",
      security: "Privacy & Security",
      automation: "Automation",
      infrastructure: "Infrastructure",
      appearance: "Appearance",
      other: "Other",
    },
    sections: {
      env: "Environment",
      auth: "Authentication",
      update: "Updates",
      meta: "Meta",
      logging: "Logging",
      diagnostics: "Diagnostics",
      cli: "CLI",
      secrets: "Secrets",
      agents: "Agents",
      models: "Models",
      skills: "Skills",
      tools: "Tools",
      memory: "Memory",
      session: "Session",
      channels: "Channels",
      messages: "Messages",
      broadcast: "Broadcast",
      notifications: "Notifications",
      talk: "Talk",
      audio: "Audio",
      commands: "Commands",
      hooks: "Hooks",
      bindings: "Bindings",
      cron: "Cron",
      approvals: "Approvals",
      security: "Security Policy",
      plugins: "Plugins",
      gateway: "Gateway",
      web: "Web",
      browser: "Browser",
      nodeHost: "Node Host",
      canvasHost: "Canvas Host",
      discovery: "Discovery",
      media: "Media",
      acp: "ACP",
      mcp: "MCP",
      theme: "Theme",
      ui: "UI",
      wizard: "Setup Wizard",
    },
    themes: {
      claw: {
        label: "Claw",
        description: "Chroma family",
      },
      knot: {
        label: "Knot",
        description: "Black & red",
      },
      dash: {
        label: "Dash",
        description: "Chocolate blueprint",
      },
    },
    textSizes: {
      small: "Small",
      default: "Default",
      large: "Large",
      xl: "XL",
      xxl: "XXL",
    },
    syncedHint: "Synced across your devices through the gateway.",
    notifications: {
      title: "Push notifications",
      nativeTitle: "Notifications",
      hint: "Receive browser push notifications from your gateway.",
      nativeHint: "Notifications are shown natively by the OpenClaw app on this Mac.",
      unavailableHint: "Not available in this browser.",
      unavailable: "Unavailable",
      checking: "Checking...",
      granted: "Granted",
      denied: "Denied",
      notRequested: "Not requested",
      unsupported: "Unsupported",
      subscribed: "Subscribed",
      notSubscribed: "Not subscribed",
      blocked: "Blocked",
      ready: "Ready",
      browserSupport: "Browser support",
      available: "Available",
      notSupported: "Not supported",
      permission: "Permission",
      status: "Status",
      unsubscribe: "Unsubscribe",
      sendTest: "Send test",
      subscribing: "Subscribing...",
      enable: "Enable notifications",
      openSystemSettings: "Open System Settings",
      blockedHint:
        "Notifications are blocked. Update your browser site permissions to allow notifications.",
      nativeBlockedHint:
        "Notifications are disabled for OpenClaw in macOS. Allow them in System Settings > Notifications.",
    },
    appearance: {
      theme: "Theme",
      chooseTheme: "Choose a theme family.",
      importedTheme: "Imported theme",
      import: "Import",
      importedFrom: "Imported from tweakcn: {name}",
      importHint: "Import a tweakcn theme into this browser-local slot",
      importFromTweakcn: "Import from tweakcn",
      tweakcnInstructions:
        "Open tweakcn.com, choose or create a theme, click Share, then paste the copied theme link here. Share links, editor URLs, registry URLs, theme IDs, and default theme names like amethyst-haze are accepted.",
      browseTweakcn: "Browse tweakcn themes",
      themeLink: "Theme link or ID",
      replace: "Replace {name}",
      importTheme: "Import theme",
      clear: "Clear {name}",
      loaded: "Loaded",
      inlineHintBefore: "Click",
      inlineHintAfter:
        "to add one browser-local tweakcn theme. In tweakcn, use Share and paste the copied link here.",
      textSize: "Text size",
    },
    chatPrefs: {
      title: "Chat",
      hint: "Browser-local chat preferences.",
    },
    sidebarPrefs: {
      title: "Sidebar",
      hint: "Choose what appears while sessions are running.",
      liveActivity: "Show live agent activity in sidebar",
      liveActivityHint: "Show the latest assistant or tool activity beneath running sessions.",
    },
    connection: {
      title: "Connection",
      gateway: "Gateway",
      status: "Status",
      assistant: "Assistant",
    },
    root: "<root>",
    itemCount: "[{count} item]",
    itemCountPlural: "[{count} items]",
    form: "Form",
    raw: "Raw",
    formUnsafeTitle: "Form view can't safely edit some fields",
    rawTitle: "Edit raw JSON/JSON5 config",
    rawUnavailableTitle: "Raw mode unavailable for this snapshot",
    open: "Open",
    applying: "Applying…",
    autoSaveSaving: "Saving…",
    autoSaveSaved: "Saved",
    autoSaveFailed: "Save failed",
    autoSaveConflict: "Settings changed elsewhere",
    retry: "Retry",
    applyBannerText: "Saved to openclaw.json — restart the gateway to apply.",
    applyBannerAction: "Restart & apply",
    rawDiscard: "Discard",
    rawDraftBlocksApply:
      "Unsaved raw config edits — save or discard them in the Raw editor before restarting.",
    rawDraftPendingFormTitle:
      "Unsaved raw config edits — save or discard them before switching to Form.",
    rawDraftBlocksFormEdit:
      "Unsaved raw config edits could not be parsed; resolve them in the Raw editor before changing settings.",
    invalidConfig: "Your configuration is invalid. Some settings may not work as expected.",
    dismissWarning: "Don't remind again",
    viewPendingChangesRaw: "View pending changes",
    rawDiffUnavailable: "Changes detected (JSON diff not available)",
    hideEnvValues: "Hide env values",
    revealEnvValues: "Reveal env values",
    peek: "Peek",
    loadingSchema: "Loading schema…",
    formUnsafe:
      "Your config contains fields the form editor can't safely represent. Use Raw mode to edit those entries.",
    rawConfig: "Raw config (JSON/JSON5)",
    secretCount: "{count} secret",
    secretCountPlural: "{count} secrets",
    redacted: "redacted",
    visible: "visible",
    revealSensitive: "Reveal sensitive values",
    hideSensitive: "Hide sensitive values",
    toggleRawRedaction: "Toggle raw config redaction",
    sensitiveHidden:
      "{count} sensitive value hidden. Use the reveal button above to edit the raw config.",
    sensitiveHiddenPlural:
      "{count} sensitive values hidden. Use the reveal button above to edit the raw config.",
  },
  execApproval: {
    expiresIn: "expires in {time}",
    expired: "expired",
    execApprovalNeeded: "Exec approval needed",
    pluginApprovalNeeded: "Plugin approval needed",
    pending: "{count} pending",
    otherPending: "Other pending requests",
    reviewRequest: "Review approval from {agent}: {command}",
    agentPendingOne: "{count} pending approval",
    agentPending: "{count} pending approvals",
    allowOnce: "Allow once",
    alwaysAllow: "Always allow",
    allowAlwaysUnavailable: "Allow Always is unavailable for this command.",
    deny: "Deny",
    labels: {
      host: "Host",
      agent: "Agent",
      session: "Session",
      cwd: "CWD",
      resolved: "Resolved",
      security: "Security",
      ask: "Ask",
      severity: "Severity",
      plugin: "Plugin",
    },
  },
  approvalPage: {
    brandName: "OpenClaw",
    eyebrow: "Operator approval",
    loadingTitle: "Loading approval",
    loadingDescription: "Checking the current approval state with the Gateway.",
    unavailableTitle: "Approval unavailable",
    unavailableDescription:
      "This approval could not be found or this device is not authorized to review it.",
    connectionErrorTitle: "Connection interrupted",
    connectionErrorDescription:
      "OpenClaw cannot confirm or record a decision while disconnected. Reconnect to check the current status.",
    retry: "Retry",
    execTitle: "Command approval",
    pending: "Waiting for your decision",
    pendingDescription: "Review the request carefully. The first answer from any surface wins.",
    approvedHere: "Approved here",
    deniedHere: "Denied here",
    resolvedElsewhere: "Resolved elsewhere",
    resolvedElsewhereDescription:
      "Another surface or an earlier attempt recorded the decision first.",
    approved: "Approved",
    denied: "Denied",
    expired: "Expired",
    cancelled: "Cancelled",
    allowedOnceDescription: "The operation was approved for this request only.",
    allowedAlwaysDescription: "The operation was approved with the always-allow decision.",
    deniedDescription: "The operation was denied and will not continue.",
    expiredDescription: "No decision arrived before the deadline, so the operation was denied.",
    cancelledDescription: "The requesting run ended before a decision could be used.",
    summaryLabel: "Summary",
    commandLabel: "Command",
    requestLabel: "Request details",
    nodeLabel: "Node",
    toolLabel: "Tool",
    expiresLabel: "Expires",
    resolvedLabel: "Resolved",
    actionsLabel: "Approval decisions",
    resolvingDecision: "Recording {decision}…",
    safeToClose: "The decision is recorded. You can close this page.",
    openControlUi: "Open Control UI",
  },
  agentTools: {
    connectedSource: "Connected: {id}",
    connected: "Connected",
    channelSource: "Channel: {id}",
    channel: "Channel",
    builtIn: "Built-In",
    plugin: "Plugin: {id}",
    optional: "Optional",
    liveNow: "Live Now",
    disabledByOverride: "Disabled by agent override.",
    enabledByProfile: "Enabled by the current profile.",
    enabledByOverride: "Enabled by agent override.",
    notIncluded: "Not included in the current profile.",
    overrideOff: "Override Off",
    enabled: "Enabled",
    overrideOn: "Override On",
    profileOff: "Profile Off",
    notLive: "Not Live",
    otherAgent: "Other Agent",
    title: "Tool Access",
    subtitle: "Profile + per-tool overrides for this agent.",
    enabledSummary: "{enabled}/{total} enabled.",
    enableAll: "Enable All",
    disableAll: "Disable All",
    loadConfig: "Load the gateway config to adjust tool profiles.",
    explicitAllowlist:
      "This agent is using an explicit allowlist in config. Tool overrides are managed in the Config tab.",
    globalAllowlist:
      "Global tools.allow is set. Agent overrides cannot enable tools that are globally blocked.",
    loadingCatalog: "Loading runtime tool catalog…",
    catalogFallback: "Could not load runtime tool catalog. Showing built-in fallback list instead.",
    availableNow: "Available Right Now",
    availableNowSubtitle: "What this agent can use in the current chat thread.",
    noSession: "no thread",
    switchAgent: "Switch chat to this agent to view its live runtime tools.",
    loadingAvailable: "Loading available tools…",
    availableError: "Could not load available tools for this thread.",
    noAvailable: "No tools are available for this thread right now.",
    moreLiveTitle: "{count} more live tools are available in the groups below.",
    moreLive: "+{count} more live tools",
    quickPresets: "Quick Presets",
    catalogTitle: "Tool Catalog",
    inherit: "Inherit",
    profile: "Profile",
    source: "Source",
    live: "Live",
    status: "Status",
    profileSourceAgent: "agent override",
    profileSourceGlobal: "global default",
    profileSourceDefault: "default",
    statusSaving: "saving…",
    statusUnsaved: "unsaved",
    statusSaved: "saved",
    toolPreview: "Tool preview",
    more: "+{count} more",
    toolsOne: "{count} Tool",
    tools: "{count} Tools",
    enabledToolsOne: "{count} Enabled Tool",
    enabledTools: "{count} Enabled Tools",
    liveToolsOne: "{count} Live Tool",
    liveTools: "{count} Live Tools",
    access: "Access",
    session: "Current Thread",
    disableNamed: "Disable {name}",
    enableNamed: "Enable {name}",
    defaultPresets: "Default Presets",
    availableVia: "Available now via {source}.",
    unavailableSession: "Not available in this chat thread right now.",
    inspectAgent: "Switch chat to this agent to inspect live availability.",
    linkTool: "Link to This Tool",
  },
  skillGroups: {
    workspace: "Workspace Skills",
    builtIn: "Built-in Skills",
    installed: "Installed Skills",
    extra: "Extra Skills",
    other: "Other Skills",
  },
  skillsPage: {
    title: "Skills",
    subtitle: "Installed skills and their status.",
    tabs: {
      all: "All",
      ready: "Ready",
      needsSetup: "Needs Setup",
      disabled: "Disabled",
    },
    defaultAgent: "{name} (default)",
    filterPlaceholder: "Filter installed skills",
    shown: "{count} shown",
    clawHub: "ClawHub",
    clawHubSubtitle: "Search and install skills from the registry",
    searchClawHub: "Search ClawHub skills…",
    searching: "Searching…",
    acknowledgeRisk: "Acknowledge risk and install",
    disconnected: "Not connected to gateway.",
    empty: "No skills found.",
    noClawHubResults: "No skills found on ClawHub.",
    install: "Install",
    installing: "Installing…",
    close: "Close",
    by: "By",
    latest: "Latest: v{version}",
    platforms: "Platforms: {platforms}",
    installNamed: "Install {name}",
    notFound: "Skill not found.",
    openDetails: "Open {name} details",
    enabledNamed: "{name} enabled",
    invalidLink: "ClawHub link invalid",
    overview: "Overview",
    skillCard: "Skill Card",
    missingRequirements: "Missing requirements",
    reason: "Reason: {reasons}",
    disabled: "Disabled",
    enabled: "Enabled",
    apiKey: "API key",
    getKey: "Get your key:",
    saveKey: "Save key",
    source: "Source:",
    refreshing: "Refreshing…",
    fullSecurityReport: "Full security report",
    loadingSkillCard: "Loading Skill Card…",
    skillCardNotLoaded: "Skill Card not loaded.",
    verdict: {
      unavailable: "Unavailable",
      clean: "Clean",
      pending: "Pending",
      blocked: "Blocked",
      review: "Review",
    },
  },
  skillStatus: {
    bundled: "bundled",
    eligible: "eligible",
    blocked: "blocked",
    disabled: "disabled",
    blockedAllowlist: "blocked by allowlist",
    blockedAgentFilter: "blocked by agent filter",
  },
  nav: {
    back: "Back",
    forward: "Forward",
    chat: "Chat",
    settings: "Settings",
    settingsGeneral: "General",
    askOpenClaw: "Ask OpenClaw",
    settingsGroupConnections: "Connections",
    settingsGroupAgents: "Agents & Tools",
    settingsGroupSecurity: "Privacy & Security",
    settingsGroupSystem: "System",
    settingsSearchLabel: "Search settings",
    settingsSearchPlaceholder: "Search settings…",
    settingsSearchNoResults: "No matching settings.",
    settingsSearchClear: "Clear settings search",
    exitSettings: "Back to app",
    close: "Close navigation",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
    resize: "Resize sidebar",
    more: "More",
    home: "Home",
    pages: "Pages",
    customize: "Edit pinned items",
    customizeReset: "Reset pinned items",
  },
  terminal: {
    title: "Terminal",
    toggle: "Toggle terminal",
    open: "Open terminal",
    hide: "Hide terminal",
    resize: "Resize terminal panel",
    newSession: "New terminal session",
    addFiles: "Add files to terminal",
    dropFiles: "Drop files to add them",
    uploading: "Uploading files…",
    uploadProgress: "Uploading {current} of {total}",
    uploadFailed: "Upload failed",
    retryUpload: "Retry",
    closeSession: "Close terminal session",
    sessions: "Terminal sessions",
    refreshSessions: "Refresh",
    loadingSessions: "Loading sessions…",
    noSessions: "No live terminal sessions",
    currentSession: "current",
    sessionAttached: "attached",
    attachSession: "Attach session",
    attachFailed: "Could not attach terminal session",
    starting: "Starting terminal…",
    connecting: "Connecting to session…",
    connectionTimedOut: "Session did not connect within 30 seconds.",
    tabLabel: "shell {n}",
    tabHint: "{agent} · {cwd}",
    agentOwnedBadge: "agent",
    exited: "exited",
    exitedCode: "exited ({code})",
    detached: "detached",
    dockBottom: "Dock to bottom",
    dockRight: "Dock to right",
    unavailable: "The terminal is not available on this gateway.",
  },
  browser: {
    title: "Browser",
    toggle: "Toggle browser panel",
    hide: "Hide browser panel",
    resize: "Resize browser panel",
    newTab: "New tab",
    closeTab: "Close tab",
    untitledTab: "New tab",
    back: "Back",
    forward: "Forward",
    reload: "Reload",
    openExternal: "Open in your browser",
    urlPlaceholder: "Enter a URL and press Enter",
    dockBottom: "Dock to bottom",
    dockRight: "Dock to right",
    annotate: "Annotate page",
    inspect: "Inspect element",
    annotateSend: "Send to chat",
    annotateUndo: "Undo",
    annotateClear: "Clear",
    annotateDone: "Exit annotate mode",
    annotateHint: "Draw on the page, then send the markup to your chat.",
    inspectHint: "Hover to inspect; click to send the element to your chat.",
    inspectName: "Name",
    inspectRole: "Role",
    inspectFocusable: "Focusable",
    loading: "Loading page…",
    starting: "Starting browser…",
    notRunning: "The gateway browser is not running.",
    start: "Start browser",
    empty: "No open tabs. Enter a URL above to browse.",
    refresh: "Refresh view",
    noChatTarget: "Open a chat thread first so the annotation has somewhere to go.",
    inspectUnavailable: "Element inspection is disabled (browser.evaluateEnabled=false).",
    annotationSent: "Annotation added to the chat composer.",
    annotatePrompt: {
      // introTitled/elementDetail (not intro/element): translated keys never
      // retranslate on source-wording changes, so the provenance-label rewrite
      // required fresh key names to propagate to all locales.
      introTitled:
        'I annotated the page at {url} (page-reported title: "{title}") — the attached screenshot shows my markup.',
      introUntitled: "I annotated the page at {url} — the attached screenshot shows my markup.",
      region:
        "Marked region {index}: centered around {x}% across / {y}% down, spanning about {width}% × {height}% of the view.",
      moreRegions: "…plus {count} more marked region(s), all visible in the screenshot.",
      elementDetail:
        "Marked element (page-reported): {descriptor} — {width}×{height}px at ({x}, {y}).",
      outro: "Please look at the marked area and tell me what you make of it.",
    },
  },
  tabs: {
    agents: "Agents",
    activity: "Activity",
    apps: "Apps",
    approvals: "Approvals",
    workboard: "Workboard",
    worktrees: "Worktrees",
    channels: "Channels",
    connection: "Connection",
    sessions: "Threads",
    usage: "Usage",
    cron: "Automations",
    tasks: "Tasks",
    skills: "Skills",
    plugins: "Plugins",
    skillWorkshop: "Skill Workshop",
    nodes: "Devices",
    chat: "Chat",
    custodian: "OpenClaw",
    config: "Config",
    profile: "Profile",
    communications: "Communications",
    appearance: "Appearance",
    automation: "Automation",
    mcp: "MCP",
    infrastructure: "Infrastructure",
    labs: "Labs",
    about: "About",
    aiAgents: "Agent Defaults",
    modelSetup: "Model Setup",
    modelProviders: "Model Providers",
    memoryImport: "Import Memory",
    notifications: "Notifications",
    security: "Privacy & Security",
    advanced: "Advanced",
    debug: "Debug",
    logs: "Logs",
    plugin: "Plugin",
  },
  subtitles: {
    agents: "Workspaces, tools, identities.",
    activity: "Browser-local tool activity summaries.",
    apps: "Companion apps for phone, watch, desktop, and browser.",
    approvals: "Recent exec, plugin, and system-agent approvals.",
    workboard: "Agent work queue and thread handoff.",
    worktrees: "Isolated agent task checkouts and recovery snapshots.",
    channels: "Channels and settings.",
    connection: "Gateway endpoint, credentials, and handshake status.",
    sessions: "Active threads and defaults.",
    usage: "API usage and costs.",
    cron: "Scheduled tasks and recurring agent runs.",
    tasks: "Background tasks: subagents, cron runs, CLI.",
    skills: "Skills and API keys.",
    plugins: "Install and manage optional capabilities.",
    skillWorkshop: "Review, refine, and apply proposals before they become live skills.",
    nodes: "Paired devices, pairing approvals, and exec bindings.",
    chat: "Gateway chat for quick interventions.",
    custodian: "System setup and care.",
    config: "Model defaults, language, and gateway host.",
    profile: "Your agent's stats, streaks, and life in the reef.",
    communications: "Channels, messages, and audio settings.",
    appearance: "Theme, UI, and setup wizard settings.",
    automation: "Commands, hooks, cron, and plugins.",
    mcp: "MCP servers, auth, tools, and diagnostics.",
    infrastructure: "Gateway, web, browser, and media settings.",
    labs: "Experimental agent and tool capabilities.",
    about: "Control UI and connected Gateway build identity.",
    aiAgents: "Global agent defaults: models, skills, tools, memory, session.",
    modelSetup: "Connect a verified AI model",
    modelProviders: "Configured providers with plan, quota, and cost.",
    memoryImport: "Bring Codex and Claude Code memory into an agent workspace.",
    notifications: "Browser push notifications from your gateway.",
    security: "Gateway auth, exec policy, tool profile, and approvals.",
    advanced: "Every remaining config section, plus the raw file editor.",
    debug: "Snapshots, events, RPC.",
    logs: "Live gateway logs.",
    plugin: "Plugin-provided panel.",
  },
  modelSetup: {
    heading: "Connect your AI",
    intro:
      "OpenClaw reuses AI access you already have — a CLI login, an API key, or a provider sign-in.",
    loading: "Checking this Gateway for available AI access…",
    retry: "Retry",
    checkAgain: "Check again",
    verify: {
      title: "Current connection",
      button: "Verify connection",
      checking: "Checking — asking {modelRef} for a quick reply…",
      answered: "Answered",
      answeredIn: "Answered in {latencyMs} ms",
    },
    access: {
      adminRequired: "Model setup requires operator.admin access.",
      gatewayTooOld: "The Gateway is running an older OpenClaw version",
    },
    candidates: {
      title: "Found on this Gateway",
      recommended: "Recommended",
      credentialsReady: "Credentials ready",
      signInNeeded: "Sign-in needed",
      detected: "Detected",
      testAndUse: "Test & use",
      testingButton: "Testing…",
      testing: "Testing — asking {modelRef} for a quick reply…",
    },
    empty: {
      title: "Recommended installs",
      intro: "No existing AI access was detected. Install one of these tools, then check again.",
    },
    unavailable: {
      title: "Detected, but not auto-tested",
    },
    signIn: {
      title: "Sign in with a provider",
      signIn: "Sign in",
      pair: "Pair",
      more: "More sign-in options",
    },
    manual: {
      title: "Connect with an API key or token",
      provider: "Provider",
      selectProvider: "Select a provider",
      accessValue: "API key or token",
      accessValuePlaceholder: "Paste an API key or token",
      connect: "Connect",
      required: "Choose a provider and enter an API key or token.",
    },
    success: {
      title: "Your AI is ready",
      detail: "{modelRef} · {latencyMs} ms",
      openChat: "Open Chat",
      configuredModel: "Configured model",
    },
    failure: {
      auth: "Authentication failed",
      rateLimit: "Rate limited",
      billing: "Billing problem",
      timeout: "Timed out",
      format: "Invalid response",
      unavailable: "Unavailable",
      unknown: "Connection failed",
    },
    errors: {
      requestFailed: "Model setup request failed.",
      activationFailed: "The model could not be activated.",
    },
    wizard: {
      dialogLabel: "Provider sign-in",
      title: "Sign in with a provider",
      starting: "Starting provider sign-in…",
      checking: "Checking your model setup…",
      working: "Working…",
      continue: "Continue",
      submit: "Submit",
      openSignIn: "Open sign-in page",
      copy: "Copy",
      expires: "Expires in {count} minutes",
      cancelled: "Provider sign-in was cancelled.",
      notComplete: "Sign-in finished, but model setup is not complete yet.",
    },
  },
  memoryImport: {
    unknownCollection: "Memory collection",
    claudeCode: "Claude Code",
    fileCountOne: "{count} file",
    fileCount: "{count} files",
    alreadyImported: "{count} existing",
    ready: "Ready",
    existing: "Existing",
    importComplete: "Import complete",
    importIncomplete: "Import incomplete",
    importedCount: "{count} files imported",
    importedWithIssues: "{migrated} imported · {errors} failed · {conflicts} conflicts",
    reportSaved: "report saved",
    recoveryFile: "Recovery file",
    recoveryJournal: "Recovery journal",
    itemBackup: "Item backup",
    codexDescription: "Consolidated Codex memory files.",
    claudeDescription: "Claude Code per-project auto-memory files.",
    providerFallback: "Import assistant memory into this agent workspace.",
    reviewFiles: "Review files",
    notFound: "Not found",
    noMemoryFound: "No importable memory found on this computer.",
    source: "Source",
    destination: "Destination",
    selectedCount: "{count} selected",
    selectAtLeastOne: "Select at least one file",
    importSelected: "Import selected",
    confirmTitle: "Import from {provider}?",
    confirmDescription: "Copy {count} selected memory files into this agent workspace.",
    confirmReplace:
      "Existing destination files will be backed up in the migration report before replacement.",
    confirmBackup: "OpenClaw creates a verified pre-import backup before copying memory.",
    confirmImport: "Import memory",
    disconnected: "Connect to the gateway to import memory.",
    title: "Import assistant memory",
    subtitle:
      "Review Codex consolidated memory and Claude Code auto-memory before copying it into OpenClaw.",
    agent: "Destination agent",
    replaceExisting: "Replace existing imports",
    replaceHint: "Preview conflicts again and preserve item backups before replacement.",
  },
  onboarding: {
    memoryImport: {
      title: "Bring your assistant memory with you",
      body: "OpenClaw found memory from other coding assistants. Import it into your agent workspace?",
      plannedCount: "{count} ready to import",
      alreadyImported: "Already imported: {count}",
      sourceUnavailable: "Source path unavailable",
      import: "Import",
      skip: "Skip",
      reviewDetails: "Review details",
      importingProvider: "Importing…",
      providerResult: "Migrated {migrated}, skipped {skipped}",
      providerIncomplete:
        "Migrated {migrated}, skipped {skipped}, {errors} failed, {conflicts} conflicts",
      providerError: "Import failed: {error}",
      connectionChanged: "Skipped: the Gateway connection changed during the import",
      unknownError: "Request failed",
      doneTitle: "Memory import finished",
      doneBody: "Migrated {migrated}, skipped {skipped}. You can continue setting up OpenClaw.",
    },
  },
  custodian: {
    title: "OpenClaw",
    subtitle: "Your system setup guide",
    subtitleCaretaker: "System setup and care.",
    exitSetup: "Exit setup",
    newAgent: "New agent",
    hatchDraft: "Wake up, my friend!",
    placeholder: "Message OpenClaw…",
    sensitivePlaceholder: "Enter sensitive value…",
    sensitiveReply: "Sensitive reply sent",
    send: "Send",
    thinking: "OpenClaw is thinking",
    earlier: "Earlier",
    requestFailed: "OpenClaw could not reply. Try again.",
    connectionChanged: "The Gateway connection changed. Retry to continue this setup.",
    unsupportedGateway: "Update the Gateway to continue setup with OpenClaw.",
    history: {
      button: "History",
      title: "Recent changes",
      description: "What changed on this system, newest first.",
      loading: "Loading recent changes…",
      empty: "No recorded changes yet.",
      requestFailed: "OpenClaw could not load change history.",
      loadMore: "Load more",
      loadingMore: "Loading more…",
      changedPaths: "Changed paths ({count})",
      invalidEdit: "This manual edit did not pass configuration validation.",
      opaqueChange: "Formatting or comments changed without visible configuration path changes.",
      sources: {
        systemAgent: "system-agent",
        doctor: "doctor",
        settings: "settings",
        cli: "CLI",
        pluginInstall: "plugin install",
        manualEdit: "manual edit",
        unknown: "unknown",
      },
    },
    nudge: {
      configReload: "Configuration reload stopped — ask me what happened",
      channelAuth: "{channel} authentication degraded — ask me what happened",
      channelDisconnected: "{channel} just disconnected — ask me what happened",
      channelDegraded: "{channel} is degraded — ask me what happened",
      channelFallback: "A channel",
      dismiss: "Dismiss this update",
    },
  },
  mcpServers: {
    add: "Add server",
    adding: "Adding…",
    nameLabel: "Name",
    targetLabel: "URL or command",
    nameInvalid: "Server names use letters, numbers, dots, dashes, or underscores.",
    targetInvalid: "Enter an http(s) URL or a command line.",
    nameTaken: "An MCP server named “{name}” already exists.",
    missing: "MCP server “{name}” was not found in the configuration.",
    missingTransport: "missing transport",
    addedSuccess: "Added MCP server {name}.",
    enabledSuccess: "Enabled MCP server {name}.",
    disabledSuccess: "Disabled MCP server {name}.",
    removedSuccess: "Removed MCP server {name}.",
    configUnavailable: "Configuration is unavailable; refresh and try again.",
    connectRequired: "Connect to the gateway to change MCP servers.",
    adminRequired: "MCP server changes require operator.admin access.",
    enable: "Enable",
    disable: "Disable",
    removeNamed: "Remove {name}",
    working: "Working…",
  },
  mcpPage: {
    connectorsLink: "Discover one-click connectors on the Plugins page.",
    servers: "Servers",
    oauth: "OAuth",
    filtered: "Filtered",
    configuredServers: "Configured servers",
    noServers: "No MCP servers configured.",
    operatorCommands: "MCP operator commands",
    operatorCommandsHint: "Status, diagnostics, auth, probing, and runtime reload.",
    runtimeHint:
      "Edits save automatically; runtime changes apply after a gateway restart, and active agents rebuild MCP runtimes on next use.",
    toolFilter: "tool filter",
    parallel: "parallel",
    tlsVerifyOff: "TLS verify off",
    mtls: "mTLS",
  },
  sessionsPage: {
    hubTablistLabel: "Threads sections",
  },
  pluginsPage: {
    searchLabel: "Search plugins",
    searchPlaceholder: "Search plugins and ClawHub",
    browseClawHub: "Browse ClawHub",
    refresh: "Refresh",
    hubTablistLabel: "Plugins sections",
    installedTab: "Installed",
    discoverTab: "Discover",
    workshopTab: "Workshop",
    tryAgain: "Try again",
    loading: "Loading plugins…",
    searching: "Searching ClawHub…",
    fromClawHub: "From ClawHub",
    noClawHubResultsBody: "ClawHub has no results for “{query}”.",
    noDiscoverMatchTitle: "Nothing to discover matches",
    featuredGroup: "Featured",
    officialGroup: "Official plugins",
    connectorsGroup: "Connect your world",
    connectorsHint:
      "One-click MCP connectors and hand-picked ClawHub searches for popular services.",
    connectorGroupWork: "Work & productivity",
    connectorGroupDev: "Coding & infrastructure",
    connectorGroupHome: "Home & media",
    connectorGroupLife: "Everyday life",
    mcp: "MCP",
    oauth: "OAuth",
    connectorMcpNote: "One-click MCP server",
    connectorClawHubNote: "Community plugins on ClawHub",
    connectorAdd: "Add",
    connectorSearch: "Find on ClawHub",
    connectorAdded: "Added",
    connectorAddedOauth: "Added {name}. Authenticate with “{command}”, then restart the gateway.",
    connectorAddedEndpoint:
      "Added {name}. Update the endpoint and credentials in MCP settings before use.",
    connectorAddedReady: "Added {name}. New agent sessions can use it right away.",
    connectorDescriptions: {
      notion: "Search, create, and update pages and databases in your Notion workspace.",
      linear: "Triage issues, update cycles, and file bugs straight from chat.",
      todoist: "Read, add, and complete tasks and projects in Todoist.",
      airtable: "Query and update records, tables, and bases in Airtable.",
      jira: "Create, search, and triage Jira tickets from chat.",
      canva: "Create and edit Canva designs, manage assets, and export results.",
      stripe: "Check payments, customers, invoices, and subscriptions in your Stripe account.",
      googleCalendar: "Read, create, and get briefed on events — your agent owns your schedule.",
      email: "Mailbox triage, summaries, and drafts with send-on-approval.",
      pdf: "Extract, merge, convert, and OCR PDF documents.",
      transcription: "Turn audio and video into clean, structured transcripts.",
      github: "PR review queues, issue triage, and repo Q&A through the official GitHub MCP.",
      sentry: "Crash alerts explained and triaged the moment they fire.",
      context7: "Version-specific library docs and code examples while coding. No signup needed.",
      deepwiki: "Ask questions about any public GitHub repo. Free, no account needed.",
      huggingFace: "Search models, datasets, and papers; run Spaces as tools.",
      grafana: "Grafana know-how and community connectors for dashboards and alerts.",
      kubernetes: "Cluster operations and troubleshooting from chat.",
      homeAssistant: "Control lights, climate, and automations across your whole home.",
      spotify: "Search, queue, and soundtrack your day with mood-based playlists.",
      sonos: "Whole-home audio: play, group rooms, and queue by chat.",
      reddit: "Browse, search, and summarize subreddits and threads.",
      markets: "Live stocks and crypto with price alerts and daily digests.",
      travel: "Flight and hotel search with fare watching and trip memory.",
      news: "A personalized daily briefing: news, weather, and tasks in one message.",
      maps: "Places, routing, and travel-time answers.",
      translation: "Translate and localize text and documents.",
      notes: "Capture notes to Markdown, Obsidian, Notion, or Bear.",
    },
    noInstalledTitle: "No optional plugins installed",
    noInstalledBody: "Discover a featured plugin or search ClawHub to extend OpenClaw.",
    noInstalledMatchTitle: "No installed plugins match",
    noMatchBody: "Try a different search.",
    filterAll: "All",
    filterIssues: "Issues",
    filterLabel: "Filter installed plugins",
    categoryChannels: "Channels",
    categoryProviders: "Model providers",
    categoryMemory: "Memory",
    categoryContextEngine: "Context engines",
    categoryTools: "Tools",
    categoryOther: "Other",
    mcpServersGroup: "MCP servers",
    mcpSettingsLink: "MCP settings",
    mcpHint:
      "Connect Model Context Protocol servers to give your agent extra tools. Changes apply to new agent sessions.",
    mcpEmpty: "No MCP servers configured yet. Add one here or pick a connector from Discover.",
    remove: "Remove",
    removing: "Removing…",
    removeNamed: "Remove {name}",
    removeConfirm: "Remove this plugin?",
    cancel: "Cancel",
    removedRestart: "Removed {name}. A Gateway restart is required to apply the change.",
    verifiedSource: "Verified source",
    enableAction: "Enable",
    disableAction: "Disable",
    working: "Working…",
    detailClose: "Close",
    detailOrigin: "Source",
    detailCategory: "Category",
    detailPackage: "Package",
    detailPluginId: "Plugin ID",
    offlineTitle: "Gateway offline",
    offlineBody: "Connect to browse installed and recommended plugins.",
    optionalCapability: "Optional OpenClaw capability.",
    enabled: "Enabled",
    disabled: "Disabled",
    available: "Available",
    needsAttention: "Needs attention",
    included: "Included",
    global: "Global",
    workspace: "Workspace",
    config: "Config",
    official: "Official",
    codePlugin: "Code plugin",
    bundlePlugin: "Bundle plugin",
    unavailable: "Unavailable",
    install: "Install",
    installing: "Installing…",
    installNamed: "Install {name}",
    enableNamed: "Enable {name}",
    disableNamed: "Disable {name}",
    acknowledgeRisk: "Acknowledge risk and install",
    defaultRiskWarning: "Review the ClawHub warning before installing this plugin.",
    connectToChange: "Connect to the gateway to change plugins.",
    adminRequired: "Browsing only. Plugin changes require operator.admin access.",
    changesDisabled: "Browsing only. This gateway does not allow plugin changes.",
    configRefreshFailed: "Could not refresh Control UI configuration: {error}",
    installedSuccess: "Installed {name}.",
    installedRestart: "Installed {name}. A Gateway restart is required to apply the change.",
    enabledSuccess: "Enabled {name}.",
    enabledRestart: "Enabled {name}. A Gateway restart is required to apply the change.",
    disabledSuccess: "Disabled {name}.",
    disabledRestart: "Disabled {name}. A Gateway restart is required to apply the change.",
  },
  labsPage: {
    intro:
      "Labs contains experimental capabilities that may change, break, or disappear between releases.",
    sectionTitle: "Experimental features",
    sectionDescription: "Changes save immediately and apply to future agent runs.",
    documentation: "Documentation",
    restartRequired: "Gateway restart required.",
    saveErrorTitle: "Could not update feature",
    saveFailed: "The feature setting could not be saved.",
    codeMode: {
      title: "Code Mode",
      description: "Let agents combine tools in compact, sandboxed JavaScript workflows.",
    },
    swarm: {
      title: "Swarm",
      description: "Let Code Mode orchestrate groups of subagents in parallel.",
      empty: "No active swarms.",
    },
  },
  aboutPage: {
    productName: "OpenClaw",
    tagline: "Your personal AI assistant, running on your own devices.",
    waveHello: "Wave hello to Clawd",
    linksLabel: "Community and resources",
    linkWebsite: "Website",
    linkDocs: "Docs",
    linkGitHub: "GitHub",
    linkDiscord: "Discord",
    linkX: "X (Twitter)",
    linkChangelog: "Changelog",
    license: "© 2026 OpenClaw Foundation — MIT License.",
    artifactTitle: "Control UI",
    artifactSubtitle: "Identity embedded when this browser artifact was built.",
    artifactDetails: "Control UI build details",
    version: "Version",
    commit: "Commit",
    branch: "Branch",
    built: "Built",
    unavailable: "Unavailable",
    copyCommit: "Copy full commit hash",
    copyingCommit: "Copying commit hash",
    copiedCommit: "Commit hash copied",
    copyCommitFailed: "Could not copy commit hash",
    gatewayVersion: "Connected Gateway version",
    gatewayVersionHint:
      "Reported by the active Gateway connection; separate from this Control UI build.",
  },
  appsPage: {
    heroTitle: "Take OpenClaw everywhere",
    heroTagline:
      "Companion apps for your phone, watch, desktop, and browser — plus plugins to extend what your agent can do.",
    sectionMobile: "On your phone",
    havePhone: "Already have the app?",
    pairDevice: "Pair your device",
    sectionWatch: "On your wrist",
    sectionDesktop: "On your desktop",
    sectionBrowser: "In your browser",
    sectionCommunity: "Community",
    badgeBundledIos: "Included with the iOS app",
    badgeBundledAndroid: "Included with the Android app",
    ctaAppStore: "App Store",
    ctaPlayStore: "Google Play",
    ctaDownload: "Download",
    ctaDocs: "Docs",
    ctaSetupGuide: "Setup guide",
    ctaOpenPlugins: "Open Plugins",
    ctaBrowseClawHub: "Browse ClawHub",
    linkDiscord: "Discord community",
    linkDocs: "Docs",
    cards: {
      ios: {
        title: "iPhone",
        desc: "Chat, talk, approve actions, and share into OpenClaw from iOS.",
      },
      android: {
        title: "Android",
        desc: "Your Android phone as a full OpenClaw node — chat, camera, and Canvas.",
      },
      appleWatch: {
        title: "Apple Watch",
        desc: "Glanceable chats and quick replies from your wrist.",
      },
      wearOs: {
        title: "Wear OS",
        desc: "The Android companion extends OpenClaw to your watch.",
      },
      macos: {
        title: "macOS",
        desc: "Menu bar companion for your Gateway — notifications, approvals, quick chat.",
      },
      windows: {
        title: "Windows",
        desc: "The Windows companion connects your PC as an OpenClaw node.",
      },
      linux: {
        title: "Linux",
        desc: "Native desktop app — .deb and AppImage builds.",
      },
      chrome: {
        title: "Chrome extension",
        desc: "Let OpenClaw drive your existing Chrome — tabs, pages, and forms.",
      },
      plugins: {
        title: "Plugins & ClawHub",
        desc: "Extend OpenClaw with channels, tools, and skills from the community.",
      },
    },
  },
  profilePage: {
    offline: "Connect to the gateway to meet your agent.",
    loading: "Diving for stats…",
    emptyTitle: "A fresh shell",
    emptyBody: "No token activity yet. Send your agent a message and watch the reef light up.",
    sinceChip: "In the reef since {date}",
    channelChipTitle: "{tokens} tokens on this channel",
    statLifetimeTokens: "Lifetime tokens",
    statPeakDay: "Peak day",
    statLongestSession: "Longest thread",
    statCurrentStreak: "Current streak",
    statLongestStreak: "Longest streak",
    streakDay: "{count} day",
    streakDays: "{count} days",
    heatmapTitle: "Token activity",
    heatmapSub: "A year in the reef — deeper red, busier claws.",
    heatmapCellTokens: "{tokens} tokens",
    legendLess: "Less",
    legendMore: "More",
    insightsTitle: "Activity insights",
    insightModel: "Most used model",
    insightMessages: "Messages exchanged",
    insightToolCalls: "Tool calls",
    insightUniqueTools: "Distinct tools",
    insightAgents: "Agents in the reef",
    insightSessions: "Threads tracked",
    sessionsCapped: "{count}+",
    toolsTitle: "Most used tools",
    toolsEmpty: "No tool runs recorded yet.",
    toolRun: "{count} run",
    toolRuns: "{count} runs",
    identity: {
      title: "Identity",
      description: "Your profile on this gateway.",
      loading: "Loading your identity…",
      profileUnavailable: "Your identity profile could not be loaded.",
      avatar: "Avatar",
      avatarDescription: "PNG, JPEG, or WebP. Images are resized to 256 × 256 or smaller.",
      chooseAvatar: "Choose image",
      processingAvatar: "Processing…",
      displayName: "Display name",
      displayNameDescription: "Shown to other people using this gateway.",
      linkedEmails: "Linked emails",
      linkedEmailsDescription: "Email addresses connected to this profile.",
      openSettings: "Open identity settings for {name}",
      avatarErrors: {
        invalid: "That image could not be processed.",
        sourceTooLarge: "Choose an image that is 10 MB or smaller.",
        tooLarge: "The processed avatar is larger than 512 KB.",
      },
    },
  },
  tasksPage: {
    active: "Active",
    activeSub: "Queued and running background work.",
    recent: "Recent",
    recentSub: "Latest completed, failed, and cancelled tasks.",
    loading: "Loading tasks…",
    empty: "No background tasks yet.",
    emptyActive: "No queued or running tasks.",
    emptyRecent: "No recent completed tasks.",
    disconnected: "Connect to the gateway to load and manage tasks.",
    loadFailed: "Could not load tasks.",
    cancelFailed: "Could not cancel the task.",
    invalidResponse: "The gateway returned an invalid task list.",
    untitled: "Background task",
    taskCount: "{count} tasks",
    taskCountOne: "1 task",
    agent: "Agent: {agent}",
    openSession: "Open thread",
    cancelTask: "Cancel {title}",
    cancelling: "Cancelling…",
    status: {
      queued: "Queued",
      running: "Running",
      completed: "Completed",
      failed: "Failed",
      cancelled: "Cancelled",
      timedOut: "Timed out",
    },
    runtime: {
      subagent: "Subagent",
      cron: "Cron",
      acp: "ACP",
      cli: "CLI",
      unknown: "Task",
    },
  },
  skillWorkshop: {
    title: "Skill Workshop",
    header: {
      useCurrentChat: "Use current chat",
      useCurrentChatAria: "Use current chat for revision requests",
      useCurrentChatTooltip:
        "Send revision requests to the current chat thread instead of the proposal's workshop thread.",
      selfLearning: "Self-learning",
      selfLearningAria: "Toggle self-learning skill proposals",
      selfLearningTooltip:
        "Capture corrections and review substantial completed work into pending skill proposals. Spends extra background tokens; drafts land on this board as pending proposals.",
      view: "Workshop view",
      board: "Board",
      today: "Today",
    },
    status: {
      all: "All",
      pending: "Pending",
      applied: "Applied",
      rejected: "Rejected",
      quarantined: "Quarantined",
      stale: "Stale",
    },
    recency: {
      today: "Today",
      yesterday: "Yesterday",
      earlier: "Earlier this week",
    },
    previewContext: "in {slug}",
    actions: {
      close: "Close",
      cancel: "Cancel",
      previous: "Previous",
      next: "Next",
      apply: "Apply",
      applying: "Applying…",
      revise: "Revise",
      tweak: "Tweak",
      opening: "Opening…",
      reject: "Reject",
      rejecting: "Rejecting…",
      sending: "Sending…",
    },
    revision: {
      title: "{verb} proposal",
      description:
        "Tell the agent what should change. The proposal stays pending and the workshop will create a revised version.",
      placeholder:
        "Example: Make this use Gmail labels instead of unread search, and add a safer dry-run step.",
      preparing: "Preparing revision handoff",
      send: "Send revision",
    },
    queue: {
      resize: "Resize proposal list",
      search: "Search proposals…",
      loadError: "Could not load proposals.",
      loading: "Loading proposals…",
      noStatus: "No {status} proposals.",
      noMatch: "No proposals match the current filter.",
    },
    detail: {
      edited: "Edited {time}",
      created: "Created {time}",
      supportFiles: "{count} support files",
      noSupportFiles: "0 support files",
      loading: "Loading proposal…",
      supportFilesTitle: "Support files",
      clickToPreview: "· click to preview",
    },
    empty: {
      searchTitle: "No matching proposals",
      searchBody: "Clear the search or try a different keyword.",
      pendingTitle: "No pending proposals",
      pendingBody: "New drafts will appear here when they need review.",
      appliedTitle: "Nothing applied yet",
      appliedBody: "Use a pending proposal and it will appear here as a live skill.",
      rejectedTitle: "No rejected proposals",
      rejectedBody: "Skipped proposals will stay here for a clean review history.",
      quarantinedTitle: "Nothing quarantined",
      quarantinedBody: "Scanner-blocked or safety-held proposals will appear here.",
      staleTitle: "No stale proposals",
      staleBody: "Proposals that can no longer apply cleanly will appear here.",
      allTitle: "No proposals here",
      allBody: "Skill Workshop proposals will appear here when your agent drafts them.",
      defaultAgent: "Your agent",
      noProposalsAria: "No Skill Workshop proposals",
      noProposalsTitle: "No proposals yet",
      noProposalsBody: "{agent} hasn't drafted any skill proposals.",
      noProposalsFooter: "New proposals will appear here for review.",
    },
    selfLearning: {
      pitchTitle: "Turn on self-learning",
      pitchBody:
        "OpenClaw reviews corrections and substantial completed runs, then drafts skill proposals for this board. It spends extra background tokens and drafts arrive as pending proposals.",
      enable: "Enable self-learning",
      enabling: "Enabling…",
      updateError: "Could not update the self-learning setting.",
    },
    history: {
      eyebrow: "Past work",
      title: "Find reusable workflows",
      body: "Review substantial threads from newest to oldest. Only strong recovery patterns or workflows that save repeated tool calls become pending proposals.",
      findIdeas: "Find skill ideas",
      scanEarlier: "Scan earlier work",
      scanNew: "Scan new work",
      scanning: "Reviewing threads…",
      loading: "Loading history…",
      pendingOnly: "Pending proposals only · uses your configured model",
      reviewed: "{count} threads reviewed",
      found: "{count} ideas found",
      noSessions: "No substantial threads found in this window.",
      today: "today",
    },
    today: {
      emptyTitle: "Nothing waiting today",
      emptyBody: "Your agent hasn't drafted anything new. Switch to Board to browse history.",
      new: "NEW",
      waiting: "WAITING",
      reviewed: "REVIEWED",
      agent: "agent",
      proposalsWaiting: "{count} proposals waiting",
      browseApplied: "Browse what's already applied.",
      progress: "{current} of {total}",
      draftedBy: "Drafted by",
      supportFile: "{count} support file",
      supportFiles: "{count} support files",
      comeWithIt: "come with it.",
      useIt: "Use it",
      addToSkills: "Add to your skills",
      tweakIt: "Tweak it",
      askAgent: "Ask the agent to change something",
      skipping: "Skipping…",
      skip: "Skip",
      notForMe: "Not for me",
      upNext: "Up next · {count} more waiting",
      seeAll: "See all proposals →",
      collection: "Your collection · {count} in use",
      manage: "Manage →",
      workflowHeading: "How the agent will use it",
      applicabilityHeading: "When the agent should use it",
    },
    relative: {
      secondsAgo: "{count}s ago",
      minutesAgo: "{count} minutes ago",
      hoursAgo: "{count}h ago",
      daysAgo: "{count}d ago",
    },
  },
  activity: {
    title: "Activity",
    subtitle: "Ephemeral agent activity derived from live session events.",
    visibleCount: "{visible} of {total}",
    filtersLabel: "Activity filters",
    search: "Search",
    searchPlaceholder: "Filter by activity, summary, run, session",
    toolFilter: "Tool",
    allTools: "All tools",
    statusFilters: "Status filters",
    autoFollow: "Auto-follow",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    clear: "Clear",
    empty: "No activity yet.",
    emptyFiltered: "No activity matches these filters.",
    entrySummary: "{argumentSummary}",
    argumentHiddenOne: "1 argument hidden",
    argumentsHidden: "{count} arguments hidden",
    streamLabel: "Agent activity entries",
    toolCallId: "Tool call",
    runId: "Run",
    session: "Session",
    outputTruncated: "Preview redacted and truncated.",
    noOutputPreview: "No output preview.",
    answerCandidate: {
      title: "Answer candidate",
      itemId: "Item",
      candidate: "Candidate answer",
      superseded: "Superseded answer",
      selected: "Selected answer",
    },
    status: {
      running: "Running",
      done: "Done",
      error: "Error",
    },
    duration: {
      ms: "{count} ms",
      seconds: "{count} s",
      minutes: "{minutes}m {seconds}s",
    },
  },
  logsView: {
    title: "Logs",
    subtitle: "Gateway file logs (JSONL).",
    exportButton: "Export {label}",
    exportLabels: {
      filtered: "filtered",
      visible: "visible",
    },
    filter: "Filter",
    searchPlaceholder: "Search logs",
    autoFollow: "Auto-follow",
    file: "File: {file}",
    truncated: "Log output truncated; showing latest chunk.",
    empty: "No log entries.",
  },
  pluginTabs: {
    unavailableTitle: "Plugin panel unavailable",
    unavailableSubtitle:
      "The plugin that owns this tab is not active on the connected gateway, or it did not provide a panel.",
  },
  logbook: {
    duration: {
      minutes: "{minutes}m",
      hours: "{hours}h {minutes}m",
    },
    nav: {
      previousDay: "Previous day",
      nextDay: "Next day",
      today: "Today",
    },
    status: {
      capturing: "Capturing every {seconds}s",
      paused: "Capture paused",
      disabled: "Capture off",
      nodeHelp: "Node providing screen snapshots.",
      pending: "{count} frames queued",
      pendingHelp: "Snapshots waiting for the next analysis batch.",
      analyzing: "Analyzing…",
      captureError: "Capture error",
      batchError: "Analysis error",
      modelMissing: "No vision model",
      modelMissingHelp:
        "Set plugins.entries.logbook.config.visionModel (for example codex/gpt-5.6-sol) or configure tools.media models.",
    },
    actions: {
      pause: "Pause",
      resume: "Resume",
      analyzeNow: "Analyze now",
    },
    empty: {
      title: "Nothing on the timeline yet.",
      subtitle:
        "Logbook is collecting snapshots; cards appear after the first analysis batch completes.",
    },
    card: {
      keyframeAlt: "Screen snapshot from this activity",
      distractions: "Distractions",
    },
    stats: {
      title: "Day at a glance",
      focus: "{pct}% focus",
      tracked: "{duration} tracked",
    },
    standup: {
      title: "Daily standup",
      generate: "Generate",
      refresh: "Regenerate",
      empty: "Turn today's timeline into a ready-to-paste standup update.",
    },
    ask: {
      title: "Ask your day",
      placeholder: "When did I review the gateway PR?",
      submit: "Ask",
    },
  },
  board: {
    label: "Thread dashboard",
    tabsLabel: "Dashboard tabs",
    moreTabs: "More dashboard tabs",
    gridLabel: "Dashboard widgets",
    emptyTitle: "A clear board, ready for work",
    emptyHint: "Your agent can pin widgets here — try asking for a status card.",
    actionFailed: "The dashboard change could not be saved.",
    actionInProgress: "Another dashboard change is still being saved.",
    announcement: {
      moved: "Moved {title}.",
      resized: "Resized {title}.",
      removed: "Removed {title}.",
      granted: "Widget access allowed.",
      rejected: "Widget access rejected.",
    },
    widget: {
      cellLabel:
        "Dashboard widget: {title}. Use arrow keys to navigate. Hold Alt and press an arrow key to move it.",
      moveHandle: "Move {title}",
      resizeHandle: "Resize {title}",
      menuLabel: "Widget options",
      moveToTab: "Move to tab",
      noOtherTabs: "No other tabs",
      resize: "Resize",
      remove: "Remove",
      needsApproval: "Needs approval",
      needsApprovalDetail: "This widget requested additional access.",
      allow: "Allow",
      reject: "Reject",
      rejected: "Access rejected",
      rejectedDetail: "This widget stays inactive until it is removed or replaced.",
      frameResolverMissing: "Widget content is unavailable.",
      errorTitle: "This widget could not load",
      errorDetail: "The problem is contained to this card.",
      actionErrorTitle: "Widget change failed",
      actionErrorDetail: "The dashboard kept the previous widget state.",
      errorShow: "Show details",
      kindMcp: "MCP",
      kindHtml: "HTML",
    },
  },
  workboard: {
    disabledHelpStart: "Workboard is disabled. Enable",
    enableConfigKey: "plugins.entries.workboard.enabled = true",
    disabledHelpEnd: ", then reload this tab.",
    status: {
      triage: "Triage",
      backlog: "Backlog",
      todo: "Todo",
      scheduled: "Scheduled",
      ready: "Ready",
      running: "Running",
      review: "Review",
      blocked: "Blocked",
      done: "Done",
    },
    noLinkedSession: "No linked thread",
    stopSession: "Stop thread",
    editCard: "Edit card",
    editCardHelp: "Update queue metadata and thread handoff.",
    newCard: "New card",
    newCardHelp: "Queue work for an agent thread.",
    archiveCard: "Archive card",
    unarchiveCard: "Restore from archive",
    archived: "Archived",
    deleteCard: "Delete card",
    viewDetails: "View details",
    detailTitle: "Card details",
    detailTask: "Gateway task",
    detailRun: "Run",
    detailUpdated: "Updated",
    detailProof: "Proof",
    detailDiagnostics: "Diagnostics",
    detailWorkerLogs: "Worker logs",
    detailWorkerProtocol: "Worker protocol",
    detailAutomation: "Automation",
    detailUpdatedValue: "Updated: {time}",
    detailAutomationTenant: "Tenant: {tenant}",
    detailAutomationBoard: "Board: {board}",
    detailAutomationSkills: "Skills: {skills}",
    detailAutomationWorkspace: "Workspace: {workspace}",
    detailAutomationSummary: "Summary: {summary}",
    detailOperatorNotes: "Operator notes",
    detailNoNotes: "No operator notes yet.",
    detailNotePlaceholder: "Add a decision, blocker, or proof note...",
    detailAddNote: "Add note",
    openSession: "Open thread",
    openLinkedSession: "Open linked thread",
    defaultAgent: "Default agent",
    allAgents: "All agents",
    agentFilter: "Filter by agent",
    allBoards: "All boards",
    boardFilter: "Filter by board",
    defaultBoard: "Default board",
    boardFilterSummary: "{active} active · {total} total",
    boardFilterArchivedSummary: "Archived · {active} active · {total} total",
    agentFilterUnassigned: "Unassigned (uses {agent})",
    agentFilterUnassignedHelp: "Cards without an explicit agent.",
    agentFilterConfiguredDefault: "{agent} (default)",
    agentFilterConfiguredDefaultHelp: "Cards explicitly assigned to the configured default agent.",
    agentCurrentUnconfigured: "{agent} (not configured)",
    viewPreset: "Workboard view",
    viewAll: "All cards",
    viewDefaultAgent: "Default agent",
    viewReady: "Ready",
    viewRunning: "Running",
    viewBlocked: "Blocked",
    viewReview: "Review",
    viewStale: "Stale",
    viewMissingProof: "Missing proof",
    viewRecentlyDone: "Recently done",
    viewPresetCount: "{count} cards",
    agentLinked: "Linked to {agent}",
    agentDefaultLinked: "Using default agent {agent}",
    engineOpenAI: "OpenAI",
    engineClaude: "Claude",
    engineDisabledRuntime:
      "{agent} uses the {runtime} ACP runtime. Use default start for that thread.",
    runEngine: "Run {engine}",
    openEngine: "Open {engine}",
    runDefaultAgent: "Run default agent",
    run: "Run",
    open: "Open",
    start: "Start",
    dependencies: "Dependencies",
    dependenciesReady: "{count} ready",
    dependenciesReadyTitle: "{count} dependencies are done.",
    dependenciesBlocked: "{count} blocked",
    dependenciesBlockedTitle: "Waiting on dependencies: {parents}.",
    dependencyMissing: "{parent} (missing)",
    dependencyStatusMissing: "Missing",
    unknownStatus: "Unknown",
    showArchived: "Show archived cards",
    hideArchived: "Hide archived cards",
    showArchivedShort: "Archived",
    hideArchivedShort: "Hide archived",
    layout: "Card layout",
    layoutCompact: "Compact card density",
    layoutComfortable: "Comfortable card density",
    dispatch: "Dispatch ready work",
    dispatchSummary:
      "Dispatch complete: started {started}, promoted {promoted}, blocked {blocked}, reclaimed {reclaimed}, orchestrated {orchestrated}, failures {failures}.",
    dispatchSummaryEmpty: "Dispatch complete: no ready work changed.",
    autoRefresh: "Auto-refresh",
    autoRefreshOff: "Off",
    autoRefresh5s: "5s",
    autoRefresh15s: "15s",
    autoRefresh30s: "30s",
    autoRefresh60s: "60s",
    lastRefreshed: "Updated {time}",
    refreshError: "Refresh failed",
    healthLabel: "Workboard health",
    healthRunning: "running",
    healthBlocked: "blocked",
    healthStale: "stale",
    healthReadyUnassigned: "ready unassigned",
    healthMissingProof: "missing proof",
    healthFailedAttempts: "failed attempts",
    ageHours: "{count}h",
    live: "live",
    fieldTitle: "Title",
    fieldNotes: "Notes",
    fieldStatus: "Status",
    fieldPriority: "Priority",
    fieldAgent: "Agent",
    fieldSession: "Thread",
    fieldLabels: "Labels",
    templatesLabel: "Card templates",
    template: {
      bugfix: "Bugfix",
      docs: "Docs",
      release: "Release",
      pr_review: "PR review",
      plugin: "Plugin",
    },
    templateDraft: {
      bugfixTitle: "Fix: ",
      bugfixNotes: "Symptom:\nCause:\nAcceptance:\nProof:",
      docsTitle: "Docs: ",
      docsNotes: "Page:\nChange:\nSource proof:",
      releaseTitle: "Release: ",
      releaseNotes: "Scope:\nVerification:\nCloseout:",
      prReviewTitle: "Review PR ",
      prReviewNotes: "Surface:\nRisks:\nProof:",
      pluginTitle: "Plugin: ",
      pluginNotes: "Boundary:\nConfig/docs:\nTests:",
    },
    badgeAttempts: "{count} attempts",
    badgeFailures: "{count} failed",
    badgeComments: "{count} comments",
    badgeLinks: "{count} links",
    badgeProof: "{count} proof",
    badgeHeartbeat: "heartbeat {age}",
    badgeArtifacts: "{count} artifacts",
    badgeAttachments: "{count} attachments",
    badgeWorkerLogs: "{count} worker logs",
    badgeWorkerProtocol: "worker {state}",
    badgeTenant: "tenant {tenant}",
    badgeSkills: "{count} skills",
    badgeDispatches: "{count} dispatches",
    badgeTaskLinked: "task linked",
    badgeClaimed: "claimed by {owner}",
    badgeDiagnostics: "{count} diagnostics",
    badgeStale: "stale",
    titlePlaceholder: "Card title",
    notesPlaceholder: "Notes, acceptance criteria, links",
    labelsPlaceholder: "ui, docs",
    searchPlaceholder: "Search cards",
    allPriorities: "All priorities",
    hideEmptyColumns: "Hide empty columns",
    emptyColumn: "Drop work here",
    emptyFilteredTitle: "No cards match this view",
    emptyFilteredHint: "Change the view, search, priority, agent, or archive filter.",
    lifecycleUnlinked: "No thread",
    lifecycleUnlinkedDetail: "Start or link a thread",
    lifecycleMissing: "Thread missing",
    lifecycleMissingDetail: "Reload threads or relink this card",
    lifecycleLinked: "Linked",
    lifecycleIdleDetail: "No active run",
    lifecycleRunning: "Running",
    lifecycleRunningDetail: "Active run in progress",
    lifecycleStale: "Stale",
    lifecycleStaleDetail: "No recent thread activity",
    lifecycleDone: "Done",
    lifecycleDoneDetail: "Moved to review",
    lifecycleNeedsReview: "Needs review",
    lifecycleNeedsReviewDetail: "Run stopped or failed",
    taskStatus: {
      queued: "Task queued",
      running: "Task running",
      completed: "Task complete",
      failed: "Task failed",
      cancelled: "Task cancelled",
      timed_out: "Task timed out",
    },
    eventsLabel: "Card events",
    eventCreated: "Created",
    eventEdited: "Edited",
    eventMoved: "Moved",
    eventMovedTo: "Moved to {status}",
    eventLinked: "Linked thread",
    eventSpecified: "Specified",
    eventDecomposed: "Decomposed",
    eventClaimed: "Claimed",
    eventHeartbeat: "Heartbeat",
    eventExecutionUpdated: "Agent updated",
    eventAttemptStarted: "Attempt started",
    eventAttemptUpdated: "Attempt updated",
    eventCommentAdded: "Comment added",
    eventLinkAdded: "Link added",
    eventProofAdded: "Proof added",
    eventArtifactAdded: "Artifact added",
    eventAttachmentAdded: "Attachment added",
    eventDiagnostic: "Diagnostic",
    eventNotification: "Notification",
    eventDispatch: "Dispatch",
    eventOrchestration: "Orchestration",
    eventProtocolViolation: "Protocol violation",
    eventArchived: "Archived",
    eventUnarchived: "Unarchived",
    eventStale: "Stale thread",
  },
  connection: {
    lostTitle: "Gateway connection lost",
    reconnecting: "Reconnecting…",
    offlineHint: "Live updates and actions are paused until the connection returns.",
    retryNow: "Retry now",
    access: {
      title: "Gateway Access",
      subtitle: "Where the dashboard connects and how it authenticates.",
      wsUrl: "WebSocket URL",
      token: "Gateway Token",
      password: "Password (not stored)",
      passwordPlaceholder: "system or shared password",
      sessionKey: "Default Session Key",
      connectHint: "Click Connect to apply connection changes.",
      trustedProxy: "Authenticated via trusted proxy.",
      showToken: "Show token",
      hideToken: "Hide token",
      toggleTokenVisibility: "Toggle token visibility",
      showPassword: "Show password",
      hidePassword: "Hide password",
      togglePasswordVisibility: "Toggle password visibility",
    },
    snapshot: {
      title: "Snapshot",
      subtitle: "Latest gateway handshake information.",
      status: "Status",
      uptime: "Uptime",
      tickInterval: "Tick Interval",
      lastChannelsRefresh: "Last Channels Refresh",
      lastError: "Last error",
    },
    help: {
      title: "How to connect",
      step1: "Start the gateway on your host machine:",
      step2: "Get a tokenized dashboard URL:",
      step3: "Paste the WebSocket URL and token above, or open the tokenized URL directly.",
      docsLink: "Read the docs →",
      copyCommand: "Copy command",
      copyCommandAria: "Copy command: {command}",
    },
  },
  attention: {
    cronFailed: "{count} cron job(s) failed",
    cronOverdue: "{count} cron job(s) overdue",
    modelAuthExpired: "Model auth expired: {providers}",
    pendingApproval: "{count} pending approval",
    pendingApprovals: "{count} pending approvals",
  },
  approvalHistory: {
    description:
      "Terminal exec, plugin, and system-agent approvals recorded by this gateway, newest first.",
    loading: "Loading approval history…",
    loadingMore: "Loading…",
    loadMore: "Load more",
    empty: "No resolved approvals in the rolling 30-day window.",
    retention: "Approval history is a rolling 30-day window.",
    offline: "Connect to the gateway to load approval history.",
    invalidResponse: "The gateway returned an invalid approval history response.",
    unknown: "Unknown",
    notApplicable: "N/A",
    columns: {
      resolved: "Resolved",
      kind: "Kind",
      request: "Request",
      decision: "Decision",
      reason: "Resolution reason",
      source: "Source agent / session",
      resolver: "Resolver",
    },
    kinds: {
      exec: "Exec",
      plugin: "Plugin",
      systemAgent: "System agent",
    },
    statuses: {
      allowed: "Allowed",
      denied: "Denied",
      expired: "Expired",
      cancelled: "Cancelled",
    },
    decisions: {
      allowOnce: "Allow once",
      allowAlways: "Always allow",
      deny: "Deny",
    },
    reasons: {
      user: "User decision",
      timeout: "Timed out",
      malformedVerdict: "Malformed verdict",
      noRoute: "No route",
      runAborted: "Run aborted",
      gatewayRestart: "Gateway restart",
      storageCorrupt: "Storage corrupt",
    },
  },
  palette: {
    placeholder: "Search chats and commands…",
    noResults: "No results",
    categories: {
      search: "Search",
      navigation: "Navigation",
      skills: "Skills",
    },
    items: {
      apps: "Apps",
      sessions: "Threads",
      scheduled: "Scheduled",
      skills: "Skills",
      plugins: "Plugins",
      settings: "Settings",
      agents: "Agents",
      shellCommand: "Shell Command",
      debugMode: "Debug Mode",
    },
    descriptions: {
      shellCommand: "Run shell",
      debugMode: "Toggle debug",
      verboseMode: "Toggle verbose mode.",
    },
    footer: {
      navigate: "navigate",
      select: "select",
      close: "close",
    },
  },
  dreaming: {
    tabs: {
      scene: "Scene",
      diary: "Diary",
      advanced: "Advanced",
    },
    header: {
      refresh: "Refresh",
      refreshing: "Refreshing…",
      on: "Dreaming On",
      off: "Dreaming Off",
    },
    restartConfirmation: {
      title: "Restart Gateway to Apply Change",
      subtitle: "Changing Dreaming mode restarts the gateway.",
      warning:
        "This action will restart the Gateway and may temporarily interrupt chats, automations, and connected channels.",
      confirm: "Confirm Restart",
      restarting: "Restarting…",
      failed: "Could not apply change. Check your connection and try again.",
    },
    status: {
      active: "Dreaming Active",
      idle: "Dreaming Idle",
      promotedSuffix: "promoted",
      nextSweepPrefix: "next sweep",
    },
    scene: {
      backfill: "Backfill",
      dedupeDiary: "Dedupe Diary",
      reset: "Reset",
      clearGrounded: "Clear Replayed",
      repairCache: "Repair Dream Cache",
      working: "Working…",
    },
    phase: {
      light: "Light",
      deep: "Deep",
      rem: "Rem",
      off: "off",
    },
    advanced: {
      eyebrow: "Review",
      title: "Daily Log Review",
      description:
        "Review what came from the daily log, what is waiting for promotion, and what was promoted recently.",
      summaryFromDailyLog: "from daily log",
      summaryWaiting: "waiting",
      summaryPromotedToday: "promoted today",
      stagedTitle: "From the Daily Log",
      stagedDescription: "Replay candidates pulled from older daily log entries.",
      shortTermTitle: "Waiting for Promotion",
      shortTermDescription: "Current short-term candidates waiting to graduate into real memory.",
      sortRecent: "Most recent",
      sortSignals: "Strongest support",
      originDailyLog: "replayed",
      originLive: "live",
      originMixed: "mixed",
      promotedTitle: "Recent Promotions",
      promotedDescription: "Items that already made it through promotion.",
      emptyGrounded: "No staged grounded replay entries right now.",
      emptyShortTerm: "No short-term entries to inspect.",
      emptyPromoted: "No recent promotions to inspect.",
      updatedPrefix: "updated",
    },
    stats: {
      shortTerm: "Short-term",
      grounded: "Grounded",
      signals: "Signals",
      promoted: "Promoted",
      phaseHits: "Phase Hits",
    },
    trace: {
      shortTerm: "Short-term",
      grounded: "Grounded",
      signals: "Signals",
      promoted: "Promoted",
      groundedLed: "grounded-led",
      emptyShortTerm: "No active short-term items.",
      emptyGrounded: "No staged grounded items.",
      emptySignals: "No active signals.",
      emptyPromoted: "Nothing promoted yet today.",
    },
    diary: {
      title: "Dream Diary",
      noDreamsYet: "No dreams yet",
      noDreamsHint: "Dreams will appear here after the first dreaming cycle runs.",
      waitingTitle: "The diary is waiting",
      waitingHint: "Narrative entries will appear after the next dreaming cycle.",
      older: "Older",
      newer: "Newer",
      reload: "Reload",
      reloading: "Reloading…",
    },
    wiki: {
      previewFallbackTitle: "Wiki page",
      close: "Close",
      loadingPage: "Loading wiki page…",
      dreamsTab: "Dreams",
      insightsTab: "Imported Insights",
      palaceTab: "Memory Palace",
      dreamsExplainer:
        "This is the raw dream diary the system writes while replaying and consolidating memory; use it to inspect what the memory system is noticing, and where it still looks noisy or thin.",
      insightsExplainer:
        "These are imported insights clustered from external history; use them to review what imports surfaced before any of it graduates into durable memory.",
      palaceExplainer:
        "This is the compiled memory wiki surface the system can search and reason over; use it to inspect actual memory pages, claims, open questions, and contradictions rather than raw imported source chats.",
      copyArchivePath: "Copy archive path",
      loadingInsights: "Loading imported insights…",
      noInsights: "No imported insights yet",
      noInsightsHint:
        "Run a ChatGPT import with apply to surface clustered imported insights here.",
      candidateSignals: "Potentially useful signals",
      corrections: "Corrections or revisions",
      importDetails: "Import details",
      startedWith: "Started with:",
      endedOn: "Ended on:",
      messages: "Messages:",
      riskReasons: "Risk reasons:",
      labels: "Labels:",
      openSourcePage: "Open source page",
      loadingPalace: "Loading memory palace…",
      emptyPalace: "Memory palace is not populated yet",
      emptyPalaceHint:
        "Right now the wiki mostly has raw source imports and operational reports. This tab becomes useful once syntheses, entities, or concepts start getting written.",
      claims: "Claims",
      openQuestions: "Open questions",
      contradictions: "Contradictions",
      pageDetails: "Page details",
      wikiPage: "Wiki page:",
      id: "Id:",
      openWikiPage: "Open wiki page",
      unavailable: "Memory Wiki is not enabled",
      unavailablePluginPrefix: "Imported Insights and Memory Palace are provided by the bundled",
      unavailablePluginSuffix: "plugin.",
      enablePrefix: "Enable",
      enableSuffix: ", then reload this tab.",
      openConfig: "Open Config",
      howToEnable: "How to enable",
    },
    phrases: {
      consolidatingMemories: "consolidating memories…",
      tidyingKnowledgeGraph: "tidying the knowledge graph…",
      replayingConversations: "replaying today's conversations…",
      weavingShortTerm: "weaving short-term into long-term…",
      defragmentingMindPalace: "defragmenting the mind palace…",
      filingLooseThoughts: "filing away loose thoughts…",
      connectingDots: "connecting distant dots…",
      compostingContext: "composting old context windows…",
      alphabetizingSubconscious: "alphabetizing the subconscious…",
      promotingHunches: "promoting promising hunches…",
      forgettingNoise: "forgetting what doesn't matter…",
      dreamingEmbeddings: "dreaming in embeddings…",
      reorganizingAttic: "reorganizing the memory attic…",
      indexingDay: "softly indexing the day…",
      nurturingInsights: "nurturing fledgling insights…",
      simmeringIdeas: "simmering half-formed ideas…",
      whisperingVectorStore: "whispering to the vector store…",
    },
  },
  modelProviders: {
    title: "Configured providers",
    subtitle: "Model providers with auth, plan, quota, and cost data.",
    updated: "Updated {time}",
    refreshing: "Refreshing…",
    disconnected: "Connect to the gateway to see configured model providers.",
    emptyTitle: "No model providers configured",
    emptySubtitle: "Sign in to a provider or add an API key, then refresh.",
    status: {
      ok: "Connected",
      expiring: "Expiring",
      expired: "Expired",
      missing: "Not signed in",
      apiKey: "API key",
    },
    expiresIn: "Credential expires in {time}",
    models: "{count} models",
    modelOne: "1 model",
    modelsAvailable: "{available} of {count} models available",
    noStats: "No live usage data reported by this provider.",
    localCost: "Session spend · {days}d",
    localCostDetail: "{tokens} tokens · {sessions} sessions",
    saving: "Saving…",
    requestFailed: "Request failed.",
    configUnavailable: "Configuration is unavailable. Refresh and try again.",
    credentials: {
      label: "Credentials",
      oauth: "OAuth profiles: {count}",
      tokenProfiles: "Token profiles: {count}",
      configKey: "API key set in config",
      envKey: "API key from environment",
      envKeyNamed: "API key from environment ({name})",
      profileKey: "API key profiles: {count}",
      none: "Not configured",
    },
    apiKey: {
      label: "API key",
      placeholder: "Enter provider API key",
      replacePlaceholder: "Secret saved. Enter a new key to replace it.",
      set: "Set API key",
      replace: "Replace key",
      remove: "Remove key",
      saved: "Secret saved.",
      removed: "API key removed.",
      authModeBlocked: 'API key changes are unavailable while auth mode is "{mode}".',
    },
    probe: {
      test: "Test connection",
      testing: "Testing…",
      unavailable: "Connection testing requires a newer gateway.",
      latency: "{ms} ms",
      status: {
        ok: "Connected",
        auth: "Authentication failed",
        rate_limit: "Rate limited",
        billing: "Billing problem",
        timeout: "Timed out",
        format: "Invalid response",
        unknown: "Connection failed",
        no_model: "No model available",
      },
    },
    logout: {
      action: "Log out",
      confirm: "Log out of {provider}? Saved OAuth and token profiles will be removed.",
      loggingOut: "Logging out…",
      done: "Logged out.",
    },
    add: {
      title: "Add provider",
      subtitle: "Choose a known model provider and save its API key.",
      action: "Add provider",
      none: "All known providers are already configured.",
      provider: "Provider",
      selectProvider: "Select a provider",
      save: "Save provider",
      saved: "Provider {provider} added.",
    },
    defaults: {
      title: "Default models",
      subtitle: "Choose the primary, ordered fallbacks, and utility model.",
      primary: "Default model",
      utility: "Utility model",
      automatic: "Automatic (provider default)",
      disabled: "Disabled",
      savedSelection: "Saved selection",
      fallbacks: "Fallbacks",
      noFallbacks: "No fallback models configured.",
      unsaved: "Unsaved changes",
      addFallback: "Add fallback",
      selectModel: "Select a model",
      selectFallback: "Select a fallback model",
      noModels: "Configure a provider before selecting default models.",
      saved: "Default models saved.",
    },
    readOnly: {
      disconnected: "Connect to the gateway to change model settings.",
      adminRequired: "Browsing only. Model changes require operator.admin access.",
    },
    notes: {
      saveKey: "Save API key for {provider} from Control UI",
      removeKey: "Remove API key for {provider} from Control UI",
      addProvider: "Add model provider {provider} from Control UI",
      defaultModel: "Update default model selection from Control UI",
    },
  },
  usage: {
    common: {
      emptyValue: "—",
      unknown: "unknown",
    },
    loading: {
      title: "Usage Overview",
      badge: "Loading",
    },
    metrics: {
      tokens: "Tokens",
      cost: "Cost",
      session: "session",
      sessions: "sessions",
    },
    providerUsage: {
      title: "Provider plans & billing",
      subtitle: "Live plan, quota, balance, and budget data reported by configured providers.",
      balance: "Balance",
      spend: "Usage",
      budget: "Budget",
      today: "Today",
      last7Days: "7 days",
      lastDays: "{count} days",
      dailyCost: "Daily provider cost",
      requests: "{count} requests",
      inputTokens: "{count} input",
      cacheTokens: "{count} cache",
      outputTokens: "{count} output",
      topModels: "Top models",
      costCategories: "Cost categories",
      remaining: "{percent}% left",
      resets: "Resets {date}",
    },
    presets: {
      today: "Today",
      last7d: "7d",
      last30d: "30d",
      last90d: "90d",
      last1y: "1y",
      all: "All",
    },
    scope: {
      instance: "Current instance",
      instanceHint: "Show only the active session id for each logical session.",
      family: "Historical lineage",
      familyHint: "Roll up known rotated transcript-backed session ids.",
      familyIncluded: "Historical lineage includes {count} session instances.",
    },
    filters: {
      title: "Filters",
      to: "to",
      startDate: "Start date",
      endDate: "End date",
      timeZone: "Time zone",
      timeZoneLocal: "Local",
      timeZoneUtc: "UTC",
      pin: "Pin",
      pinned: "Pinned",
      unpin: "Unpin filters",
      selectAll: "Select All",
      clear: "Clear",
      clearAll: "Clear All",
      remove: "Remove filter",
      removeDays: "Remove days filter",
      removeHours: "Remove hours filter",
      removeSession: "Remove session filter",
      all: "All",
      days: "Days",
      hours: "Hours",
      session: "Session",
      agent: "Agent",
      channel: "Channel",
      provider: "Provider",
      model: "Model",
      tool: "Tool",
      daysCount: "{count} days",
      hoursCount: "{count} hours",
      sessionsCount: "{count} sessions",
    },
    query: {
      placeholder:
        "Filter sessions (e.g. key:agent:main:cron* model:gpt-4o has:errors minTokens:2000)",
      apply: "Filter (client-side)",
      matching: "{shown} of {total} sessions match",
      inRange: "{total} sessions in range",
      tip: "Tip: use filters or click bars to refine days.",
    },
    export: {
      label: "Export",
      sessionsCsv: "Sessions CSV",
      dailyCsv: "Daily CSV",
      json: "JSON",
    },
    cacheStatus: {
      warning: "Usage cache is rebuilding in the background. Displayed totals may be stale.",
      title: "{status}: {pending} pending, {stale} stale, {cached} cached",
      status: {
        refreshing: "refreshing",
        stale: "stale",
        partial: "partial",
      },
    },
    empty: {
      title: "Start with a date range",
      subtitle:
        "Load usage data to compare costs, inspect sessions, and drill into timelines without leaving the dashboard.",
      hint: "Select a date range and click Refresh to load usage.",
      noData: "No data",
      featureOverview: "Overview cards",
      featureSessions: "Session ranking",
      featureTimeline: "Timeline drilldown",
    },
    daily: {
      title: "Daily Usage",
      total: "Total",
      byType: "By Type",
      tokensTitle: "Daily Token Usage",
      costTitle: "Daily Cost",
      compressedScaleHint: "Square-root scale keeps low-usage days visible.",
    },
    costWindows: {
      title: "Cost Windows",
      subtitle: "Calendar windows ending {date}",
      selectedRange: "Selected Range",
      lastDays: "Last {count} days",
      perDay: "/ day",
    },
    breakdown: {
      output: "Output",
      input: "Input",
      cacheWrite: "Cache Write",
      cacheRead: "Cache Read",
      total: "Total",
      tokensByType: "Tokens by Type",
      costByType: "Cost by Type",
    },
    overview: {
      title: "Usage Overview",
      messages: "Messages",
      messagesHint: "Total user and assistant messages in range.",
      messagesAbbrev: "msgs",
      user: "user",
      assistant: "assistant",
      toolCalls: "Tool Calls",
      toolCallsHint: "Total tool call count across sessions.",
      toolsUsed: "tools used",
      errors: "Errors",
      errorsHint: "Total message and tool errors in range.",
      toolResults: "tool results",
      avgTokens: "Avg Tokens / Msg",
      avgTokensHint: "Average tokens per message in this range.",
      avgCost: "Avg Cost / Msg",
      avgCostHint: "Average cost per message when providers report costs.",
      avgCostHintMissing:
        "Average cost per message when providers report costs. Cost data is missing for some or all threads in this range.",
      acrossMessages: "Across {count} messages",
      sessions: "Threads",
      sessionsHint: "Distinct threads in the range.",
      sessionsInRange: "of {count} in range",
      throughput: "Throughput",
      throughputHint: "Throughput shows tokens per minute over active time. Higher is better.",
      tokensPerMinute: "tok/min",
      perMinute: "/ min",
      errorRate: "Error Rate",
      errorHint: "Error rate = errors / total messages. Lower is better.",
      avgSession: "avg session",
      cacheHitRate: "Cache Hit Rate",
      cacheHint:
        "Cache hit rate = cache read / (input + cache read + cache write). Higher is better.",
      cached: "cached",
      prompt: "prompt",
      calls: "calls",
      costShare: "{percent}% of cost",
      topModels: "Top Models",
      topProviders: "Top Providers",
      topTools: "Top Tools",
      topAgents: "Top Agents",
      topChannels: "Top Channels",
      peakErrorDays: "Peak Error Days",
      peakErrorHours: "Peak Error Hours",
      noModelData: "No model data",
      noProviderData: "No provider data",
      noToolCalls: "No tool calls",
      noAgentData: "No agent data",
      noChannelData: "No channel data",
      noErrorData: "No error data",
    },
    sessions: {
      title: "Sessions",
      shown: "{count} shown",
      total: "{count} total",
      avg: "avg",
      all: "All",
      recent: "Recently viewed",
      recentShort: "Recent",
      sort: "Sort",
      ascending: "Ascending",
      descending: "Descending",
      clearSelection: "Clear Selection",
      noRecent: "No recent sessions",
      noneInRange: "No sessions in range",
      more: "+{count} more",
      selected: "Selected ({count})",
      copy: "Copy",
      copyName: "Copy session name",
      limitReached: "Showing first 1,000 sessions. Narrow date range for complete results.",
    },
    details: {
      noUsageData: "No usage data for this session.",
      duration: "Duration",
      modelMix: "Model Mix",
      filtered: "(filtered)",
      close: "Close session details",
      noTimeline: "No timeline data",
      loadFailed: "Could not load {detail}: {error}",
      noDataInRange: "No data in range",
      usageOverTime: "Usage Over Time",
      reset: "Reset",
      perTurn: "Per Turn",
      cumulative: "Cumulative",
      turnRange: "Turns {start}–{end} of {total}",
      assistantOutputTokens: "Assistant output tokens",
      userToolInputTokens: "User + tool input tokens",
      tokensWrittenToCache: "Tokens written to cache",
      tokensReadFromCache: "Tokens read from cache",
      noContextData: "No context data",
      systemPromptBreakdown: "System Prompt Breakdown",
      collapse: "Collapse",
      collapseAll: "Collapse All",
      expandAll: "Expand All",
      baseContextPerMessage: "Base context per message",
      system: "System",
      systemShort: "Sys",
      skills: "Skills",
      tools: "Tools",
      files: "Files",
      ofInput: "of input",
      of: "of",
      timelineFiltered: "timeline filtered",
      conversation: "Conversation",
      noMessages: "No messages",
      tool: "Tool",
      toolResult: "Tool result",
      hasTools: "Has tools",
      searchConversation: "Search conversation",
      filterByRole: "Filter by role",
      filterByTool: "Filter by tool",
      you: "You",
      noMessagesMatch: "No messages match the filters.",
    },
    mosaic: {
      title: "Activity by Time",
      subtitleEmpty: "Estimates require session timestamps.",
      subtitle: "Estimated from session spans (first/last activity). Time zone: {zone}.",
      noTimelineData: "No timeline data yet.",
      dayOfWeek: "Day of Week",
      midnight: "Midnight",
      fourAm: "4am",
      eightAm: "8am",
      noon: "Noon",
      fourPm: "4pm",
      eightPm: "8pm",
      legend: "Low → High token density",
      sun: "Sun",
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
      sat: "Sat",
    },
  },
  login: {
    subtitle: "Gateway Dashboard",
    passwordPlaceholder: "optional",
    showToken: "Show token",
    hideToken: "Hide token",
    toggleTokenVisibility: "Toggle token visibility",
    showPassword: "Show password",
    hidePassword: "Hide password",
    togglePasswordVisibility: "Toggle password visibility",
    failure: {
      rawError: "Raw error",
      docsAuth: "Control UI auth docs",
      docsPairing: "Device pairing docs",
      docsInsecure: "Insecure HTTP docs",
      authRequired: {
        title: "Auth required",
        summary:
          "The Gateway is reachable, but it needs a matching token or password before this browser can connect.",
        stepPaste:
          "Paste the token from openclaw dashboard --no-open or enter the configured password.",
        stepGenerate:
          "If no token is configured, run openclaw doctor --generate-gateway-token on the gateway host.",
        stepConnect: "Click Connect again after updating the credential.",
      },
      authFailed: {
        title: "Auth did not match",
        summary:
          "The supplied credential was rejected. The most common cause is a stale token or a token copied from another Gateway URL.",
        stepDashboard:
          "Run openclaw dashboard --no-open and open the fresh URL or paste its token.",
        stepReplace:
          "Replace stale token/password values; do not reuse a token from another Gateway URL.",
        stepMode:
          "Use one matching auth mode at a time: gateway token for token mode, password for password mode.",
      },
      rateLimited: {
        title: "Too many failed attempts",
        summary: "The Gateway is temporarily limiting authentication attempts for this client.",
        stepStop: "Stop retrying from this tab for a moment.",
        stepWait:
          "Wait for the auth limiter to cool down, then reconnect with the corrected credential.",
        stepCheckClients: "If this is a shared host, check other clients for repeated bad retries.",
      },
      pairing: {
        title: "Device pairing required",
        scopeTitle: "Scope upgrade pending",
        roleTitle: "Role upgrade pending",
        metadataTitle: "Device refresh pending",
        summary:
          "This browser needs one-time approval from the Gateway host before it can use the Control UI.",
        upgradeSummary:
          "This browser is already known, but the requested access changed and needs a fresh approval.",
        stepList: "Run openclaw devices list on the Gateway host.",
        stepApproveId: "Approve this request: openclaw devices approve {requestId}.",
        stepApprove: "Approve the pending browser/device request from that list.",
        stepReconnect: "Reconnect after the approval completes.",
      },
      insecure: {
        title: "Secure browser context required",
        summary:
          "This page is running over plain HTTP, so the browser cannot create the device identity the Gateway expects.",
        stepHttps: "Use HTTPS/Tailscale Serve, or open http://127.0.0.1:18789 on the Gateway host.",
        stepLocalCompat:
          "For local token-only compatibility, set gateway.controlUi.allowInsecureAuth: true.",
        stepAvoidDisable: "Avoid disabling device auth for remote HTTP access.",
      },
      origin: {
        title: "Browser origin not allowed",
        summary:
          "The Gateway rejected this page origin before accepting the Control UI connection.",
        stepAllowedOrigins: "Add this browser origin to gateway.controlUi.allowedOrigins.",
        stepFullOrigin: "Use full origins such as http://localhost:5173, not wildcard patterns.",
        stepRestart: "Restart or reload the Gateway after changing allowed origins.",
      },
      protocol: {
        title: "Protocol mismatch",
        summary:
          "The served Control UI and the running Gateway do not agree on the supported connection protocol.",
        stepDashboard:
          "Reopen the served dashboard with openclaw dashboard so the UI and Gateway come from the same install.",
        stepDevUi:
          "If using pnpm ui:dev, rebuild or restart the dev UI against the current checkout.",
        stepRestart:
          "Restart the Gateway after updating OpenClaw so it serves the current protocol.",
      },
      network: {
        title: "Could not connect",
        summary:
          "The browser could not complete the Gateway connection. Check the target and transport before retrying credentials.",
        stepGateway: "Confirm the Gateway is running with openclaw status or openclaw gateway run.",
        stepUrl:
          "Check the WebSocket URL and use wss:// when the Gateway is behind HTTPS/Tailscale Serve.",
        stepDashboard:
          "Reopen the dashboard with openclaw dashboard --no-open to recopy the current URL and auth details.",
      },
    },
  },
  chat: {
    disconnected: "Disconnected from gateway.",
    archivedSessionDisabled: "Restore this thread to send messages.",
    loadOlder: "Load older",
    sessionHeader: {
      renameTooltip: "Rename thread",
      renameAria: "Rename thread {title}",
      renameInputAria: "Thread title",
      renameInputPlaceholder: "Thread title",
      workspaceAria: "Workspace actions for {workspace}",
      revealFinder: "Reveal in Finder",
      revealFileExplorer: "Reveal in File Explorer",
      revealFileManager: "Open in file manager",
      copyPath: "Copy path",
      copyBranch: "Copy branch name",
      copied: "Copied",
      branches: "Thread branches",
      branchSwitchUnavailable: "Branch switch is unavailable while the agent is working.",
      branchSwitchRequiresAdmin: "Branch switching requires operator admin access.",
      untitledBranch: "Untitled branch",
      oneMessage: "{count} message",
      messages: "{count} messages",
      activeBranch: "Active branch",
    },
    board: {
      faceLabel: "Thread face",
      chatFace: "Chat",
      dashboardFace: "Dashboard",
      dockMenu: "Chat dock: {dock}",
      dockLeft: "Dock chat left",
      dockRight: "Dock chat right",
      dockBottom: "Dock chat bottom",
      dockHidden: "Hide chat",
      resizeDock: "Resize chat dock",
      reopenChat: "Show chat",
      mockPlaceholder: "Board view seam · {tabs} tabs · {widgets} widgets",
      mockOverview: "Overview",
      mockResearch: "Research",
      mockSessionStatus: "Thread status",
      mockRecentFindings: "Recent findings",
      mockSourceMap: "Source map",
      resetTitle: "Reset conversation?",
      resetDescription: "The conversation context resets. Your dashboard stays.",
    },
    catalog: {
      remoteViewOnly: "This thread is on a paired node and is view-only.",
      unsupportedViewOnly: "This external thread source is view-only.",
      sessionMenu: "External thread actions",
      openInOpenClaw: "Open in OpenClaw",
      openInTerminal: "Open in terminal",
      terminalUnavailable: "Terminal opening is unavailable for this thread.",
    },
    taskSuggestions: {
      eyebrow: "Suggested follow-up",
      start: "Start in worktree",
      starting: "Starting…",
      dismiss: "Dismiss {title}",
      project: "Project",
      instructions: "Instructions",
      adminRequired: "Administrator access is required to create a worktree from this project.",
    },
    pullRequests: {
      linkLabel: "Pull request #{number}: {title}",
      createPr: "Create PR",
      createPrLabel: "Create a pull request for {branch}",
      dismiss: "Dismiss pull request #{number}",
      open: "Open",
      draft: "Draft",
      merged: "Merged",
      closed: "Closed",
      checks: "CI",
      checksPassing: "CI checks passing",
      checksFailing: "CI checks failing",
      checksPending: "CI checks running",
      ciMonitoring: "CI monitoring",
      openChecks: "Open checks on GitHub",
      checksPassed: "Passed",
      checksFailed: "Failed",
      checksRunning: "Running",
      checksSkipped: "Skipped",
      showMore: "Show {count} more",
      rateLimited:
        "GitHub API rate limit reached. Pull request status may be out of date until the limit resets.",
    },
    usageRemaining: "Usage Remaining",
    view: {
      menu: "View",
      reasoning: "Reasoning",
      toolCalls: "Tool calls",
      commentary: "Keep commentary",
    },
    sendShortcut: "Send shortcut",
    sendShortcutEnter: "Enter",
    sendShortcutModifierEnter: "⌘/Ctrl+Enter",
    followUpMode: "Follow-ups while the agent is working",
    followUpModeQueue: "Queue until the run ends",
    followUpModeServer: "Server default ({mode})",
    followUpModeLoading: "loading…",
    followUpModeSteer: "Steer into the active run",
    followUpModeUsingServer: "Using server default ({mode})",
    followUpModeOverriding: "Overriding server default ({mode})",
    followUpModeReset: "Reset to server default",
    catalogOpenTarget: "Open Codex/Claude threads in",
    catalogOpenTargetViewer: "OpenClaw viewer",
    catalogOpenTargetTerminal: "Terminal",
    onboardingDisabled: "Disabled during setup",
    gatewayStatus: "Gateway status: {status}",
    commandPaletteTitle: "Search or jump to… (⌘K)",
    openCommandPalette: "Open command palette",
    docsOpensInNewTab: "{label} (opens in new tab)",
    updating: "Updating…",
    dismissUpdateBanner: "Dismiss update banner",
    switchedSession: "Switched to {session}",
    actions: {
      dismissError: "Dismiss error",
      exitFocusMode: "Exit focus mode",
      scrollToLatest: "Scroll to latest",
    },
    compaction: {
      label: "Compacted history",
      savedTokens: "saved {count} tokens",
      description:
        "The compacted transcript is preserved as a checkpoint. Open thread checkpoints to branch or restore from that compacted view.",
      openCheckpoints: "Open checkpoints",
    },
    progressLabels: {
      shelling: "Shelling",
      scuttling: "Scuttling",
      clawing: "Clawing",
      pinching: "Pinching",
      molting: "Molting",
      bubbling: "Bubbling",
      tiding: "Tiding",
      reefing: "Reefing",
      cracking: "Cracking",
      sifting: "Sifting",
      brining: "Brining",
      nautiling: "Nautiling",
      krilling: "Krilling",
      barnacling: "Barnacling",
      lobstering: "Lobstering",
      tidepooling: "Tidepooling",
      pearling: "Pearling",
      snapping: "Snapping",
      surfacing: "Surfacing",
    },
    commands: {
      arguments: "Command arguments",
      menu: "Slash commands",
      instant: "instant",
      optionCount: "{count} options",
      showMoreOne: "Show 1 more command",
      showMoreMany: "Show {count} more commands",
      navigate: "navigate",
      fill: "fill",
      run: "run",
      select: "select",
      close: "close",
      clearDescription: "Clear chat history",
      redirectDescription: "Abort and restart with a new message",
      steerDescription: "Inject a message into the active run",
      categories: {
        session: "Thread",
        model: "Model",
        agents: "Agents",
        tools: "Tools",
      },
    },
    splitView: {
      open: "Open split view",
      splitRight: "Split right",
      splitDown: "Split down",
      closePane: "Close pane",
      dropSplit: "Split",
      dropOpenHere: "Open here",
    },
    sidebar: {
      updateAvailable: "Update available",
      updateMacAndGateway: "Update Mac app + Gateway",
      updateGateway: "Update Gateway",
      allSessions: "All threads",
      threads: "Threads",
      groups: "Groups",
      coding: "Coding",
      groupCatalogSessionsByProject: "Group by project",
      openSessionMenu: "Open thread menu",
      sortBy: "Sort by",
      sortCreated: "Created",
      sortSessions: "Sort threads",
      sortUpdated: "Last updated",
      sessionMenu: "Actions for {session}",
      sessionMenuMany: "Actions for {count} threads",
      toolActivity: "Using {tool}",
    },
    welcome: {
      hintBeforeShortcut: "Type a message below ·",
      hintAfterShortcut: "for commands",
      recentSessions: "Recent chats",
      suggestions: {
        whatCanYouDo: "What can you do?",
        summarizeRecentSessions: "Summarize my recent threads",
        configureChannel: "Help me configure a channel",
        checkSystemHealth: "Check system health",
      },
    },
    runControls: {
      newSession: "New thread",
      newSessionDisconnected: "Connect to create a new thread",
      newSessionWorktree: "New thread in worktree",
      export: "Export",
      exportChat: "Export chat",
      queue: "Queue",
      queueMessage: "Queue message",
      stop: "Stop",
      stopGenerating: "Stop generating",
      send: "Send",
      sendMessage: "Send message",
    },
    queue: {
      retry: "Retry",
      retrySend: "Retry send",
      retryQueuedMessage: "Retry queued message",
      steer: "Steer",
      steered: "Steered",
      steerQueuedMessage: "Steer queued message",
      removeQueuedMessage: "Remove queued message",
    },
    goals: {
      edit: "Edit goal",
      pause: "Pause goal",
      resume: "Resume goal",
      clear: "Clear goal",
    },
    questions: {
      title: "Codex needs input",
      other: "Type your own answer here",
      submit: "Submit",
      next: "Next",
      back: "Back",
      skip: "Skip",
      skipping: "Skipping…",
      skipped: "Skipped",
      collapse: "Collapse question",
      expand: "Expand question",
      eyebrow: "Question",
      cardLabel: "Question from the assistant",
      summaryLabel: "Question outcome",
      timeRemaining: "Time remaining",
      ownAnswer: "Your own answer…",
      ownAnswerFor: "Your own answer for {header}",
      submitting: "Submitting…",
      submitFailed: "Could not submit: {error}",
      answered: "Answered",
      answeredElsewhere: "Answered elsewhere",
      unavailable: "Unavailable",
      expired: "Expired",
      cancelled: "Cancelled",
    },
    messages: {
      activity: "Activity",
      forkFromHere: "Fork from here",
      hide: "Hide",
      hideConfirm: "Hide this message in this browser? The agent still sees it.",
      hideMessage: "Hide message",
      hideTooltip: "Hide in this browser only",
      openInCanvas: "Open in canvas",
      rewind: "Rewind",
      rewindConfirm: "Rewind to before this message?",
      rewindToHere: "Rewind to here",
      rewindUnavailable: "Rewind is unavailable while the agent is working",
      forkUnavailable: "Fork is unavailable while the agent is working",
      unknownDate: "Unknown date",
      voiceNote: "Voice note",
    },
    modelControls: {
      default: "Default",
      reasoning: "Reasoning",
      speed: "Speed",
      sessionOverride: "Thread override",
      usingDefault: "Using default from Settings",
      resetToDefault: "Reset to default ({model})",
    },
    sideChat: {
      title: "Side chat",
      notSaved: "Not saved to chat history",
      thinking: "Thinking…",
      clear: "Clear side chat",
      close: "Close side chat",
      followUp: "Follow up…",
      followUpLabel: "Follow up in side chat",
      sendFollowUp: "Send follow-up",
    },
    detailPanel: {
      close: "Close sidebar",
      copyPath: "Copy path",
      discard: "Discard",
      editFile: "Edit file",
      searchInFile: "Search in file",
      showInFiles: "Show in Files",
      previousMatch: "Previous match",
      nextMatch: "Next match",
      overwrite: "Overwrite",
      viewRawText: "View Raw Text",
      renderedMarkdown: "Rendered Markdown",
      renderedMarkdownHint: "Sanitized rich-text preview for quick reading.",
      noPreviewableMarkdown: "No previewable markdown content.",
      noContent: "No content available",
    },
    thread: {
      search: "Search messages",
      searchPlaceholder: "Search messages...",
      closeSearch: "Close search",
      unpin: "Unpin",
      loading: "Loading chat",
      noMatches: "No matching messages",
    },
    pairingQrExpired: {
      title: "Pairing QR expired",
      reason: "Run /pair qr again to generate a fresh setup code.",
      badge: "Expired",
    },
    composer: {
      placeholder: "Message {name}",
      placeholderWithAttachments: "Add a message or paste more images...",
      placeholderDisconnected: "Connect to the gateway to start chatting...",
      preparingModel: "Preparing model...",
      responding: "{name} is responding...",
      sendingMessage: "Sending message...",
      working: "{name} is working...",
      runDone: "Done",
      runInterrupted: "Interrupted",
      runStatus: "Run status: {status}",
      cancelReply: "Cancel reply",
      compactRecommendedContext: "Compact recommended thread context",
      removeAttachment: "Remove attachment",
      addAttachment: "Add attachment",
      attachPhoto: "Photo",
      attachFile: "Attach file",
      attachFileOption: "File",
      contextUsage: {
        title: "Context usage details",
        open: "Open context usage details",
        summary: "Thread context usage: {used} of {limit} ({pct}%)",
        contextWindow: "Context window",
        latestRunTokens: "Latest run tokens",
        estimatedCost: "Est. cost",
        planUsage: "Plan usage",
        openUsage: "Open usage dashboard",
        resets: "Resets {time}",
        usageCredits: "Usage credits",
        budgetValue: "{used} of {limit}",
        limitFiveHour: "5-hour limit",
        limitWeekly: "Weekly · all models",
        limitDaily: "Daily limit",
        limitHours: "{hours}-hour limit",
      },
      takePhoto: "Take photo",
      cameraAccessFailed: "Unable to access the camera.",
      cameraBusy: "The camera is busy or unavailable to the browser.",
      cameraFallback: "Camera {number}",
      cameraInput: "Camera",
      cameraListUnsupported: "This browser cannot list cameras.",
      cameraNoneFound: "No camera was found.",
      cameraPageInactive: "Cameras are unavailable while this page is inactive.",
      cameraPermissionBlocked:
        "Camera access is blocked. Allow camera access in browser site settings.",
      cameraPreview: "Camera preview",
      dismissVoiceInputError: "Dismiss voice input error",
      microphoneAccessFailed: "Unable to access microphone inputs.",
      microphoneBusy: "Microphone inputs are busy or unavailable to the browser.",
      microphoneFallback: "Microphone {number}",
      microphoneInput: "Microphone input",
      microphoneAppliesNextSession: "Changes apply when you start your next Talk session.",
      microphoneListUnsupported: "This browser cannot list microphone inputs.",
      noCameras: "No additional cameras found",
      noMicrophones: "No additional microphones found",
      microphoneNoneFound: "No microphone inputs were found.",
      microphonePageInactive: "Microphone inputs are unavailable while this page is inactive.",
      microphonePermissionBlocked:
        "Microphone access is blocked. Allow it in browser site settings to list inputs.",
      holdToRecordSetting: "Hold microphone button to dictate",
      holdToRecordSettingDescription:
        "Hold the composer microphone button, speak, then release to insert text without sending.",
      dictationAudioUnsupported: "The Gateway returned an unsupported dictation audio format.",
      dictationBrowserAudioUnsupported: "This browser cannot capture dictation audio at 8 kHz.",
      dictationConnecting: "Starting dictation…",
      dictationDisconnected: "Dictation stopped because the Gateway disconnected.",
      dictationFailed: "Dictation failed.",
      dictationFinalizing: "Finishing dictation…",
      dictationFinalizationTimedOut:
        "Dictation stopped before the last partial transcript could be finalized.",
      dictationProviderUnavailable: "No transcription provider is configured for dictation.",
      dictationRecording: "Recording {elapsed}",
      dictationReleaseToInsert: "Release to insert dictation",
      realtimeTalkRequiresMicrophone: "Realtime voice input requires browser microphone access.",
      selectedMicrophoneUnavailable:
        "The selected microphone is unavailable. Choose another input or System default.",
      selectedCameraUnavailable:
        "The selected camera is unavailable. Choose another camera or System default.",
      startVoiceInput: "Start voice input",
      stillListening: "Still listening",
      stopVoiceInput: "Stop voice input",
      systemDefaultMicrophone: "System default",
      systemDefaultCamera: "System default",
      switchCamera: "Switch camera",
      turnCameraOff: "Turn camera off",
      turnCameraOn: "Turn camera on",
      voiceTranscript: "Voice transcript",
    },
    selectors: {
      session: "Chat thread",
      sessionSearch: "Search threads",
      clearSessionSearch: "Clear thread search",
      loadMoreSessions: "Load more threads",
      model: "Chat model",
      modelSection: "Model",
      modelLocked: "Locked",
      modelLockedLabel: "Model selection controlled for this thread",
      lockedSessionModel: "Thread model",
      nativeCodexModel: "Codex-controlled model",
      thinkingLevel: "Chat thinking level",
    },
    toolCards: {
      tool: "Tool",
      canvas: "Canvas",
      summary: "Summary",
      toolInput: "Tool input",
      toolOutput: "Tool output",
      toolError: "Tool error",
      rawDetails: "Raw details",
      openFile: "Open file",
      openDetails: "Open tool details in side panel",
      widgetActions: "Widget actions",
      copyToClipboard: "Copy to clipboard",
      downloadFile: "Download file",
      widgetExportRerender: "This widget needs to be re-rendered to export as an image.",
      widgetExportFailed: "Widget export failed. Try again.",
      fileChanges: "File changes",
      attemptedChanges: "Attempted changes",
      failed: "failed",
      running: "Running",
      noOutputFailed: "No output — tool failed.",
      noOutputSucceeded: "No output — tool completed successfully.",
      noResult: "No result available.",
      verbs: {
        read: "Read",
        edit: "Edit",
        editing: "Editing",
        edited: "Edited",
        write: "Write",
        writing: "Writing",
        wrote: "Wrote",
        searched: "Searched",
        fetched: "Fetched",
      },
      group: {
        commandsOne: "ran a command",
        commandsMany: "ran {count} commands",
        readsOne: "read a file",
        readsMany: "read {count} files",
        editsOne: "edited a file",
        editsMany: "edited {count} files",
        writesOne: "created a file",
        writesMany: "created {count} files",
        searchesOne: "ran a search",
        searchesMany: "ran {count} searches",
        fetchesOne: "fetched a page",
        fetchesMany: "fetched {count} pages",
        namedTool: "used {names}",
        namedToolRepeated: "used {names} ×{count}",
        otherOne: "used a tool",
        otherMany: "used {count} tools",
        emptyOne: "Ran a tool call",
        emptyMany: "Ran {count} tool calls",
        failedOne: "{count} failed",
        failedMany: "{count} failed",
        activityErrorOne: "Activity: {count} tool, includes errors.",
        activityErrorMany: "Activity: {count} tools, includes errors.",
      },
    },
    workRun: {
      workedFor: "Worked for {duration}",
      worked: "Worked",
      workedForError: "Worked for {duration}, includes errors.",
      workedError: "Worked, includes errors.",
    },
    backgroundTasks: {
      label: "Background tasks",
      title: "Background tasks",
      show: "Show background tasks",
      collapse: "Collapse background tasks",
      refresh: "Refresh background tasks",
      loading: "Loading background tasks…",
      empty: "No background tasks for this agent yet.",
      running: "Running ({count})",
      finished: "Finished ({count})",
      statusRunningOne: "1 running task",
      statusRunningMany: "{count} running tasks",
      statusPreviewMore: "+{count} more",
      stopTask: "Stop {title}",
      viewTranscript: "View transcript",
      toolUseOne: "1 tool use",
      toolUseMany: "{count} tool uses",
      expandTask: "Show details for {title}",
      collapseTask: "Hide details for {title}",
      detailLoading: "Loading task details…",
      detailFailed: "Could not load task details.",
      prompt: "Prompt",
      output: "Output",
      promptUnavailable: "Prompt unavailable.",
      outputPending: "No output yet.",
    },
    sessionDiff: {
      title: "Changes",
      show: "Show thread changes",
      refresh: "Refresh changes",
      loading: "Loading changes…",
      empty: "No changes in this thread's checkout.",
      notGit: "This thread's workspace is not a git checkout.",
      unknownSession: "No workspace is associated with this thread.",
      disconnected: "Gateway is disconnected.",
      unmodifiedLines: "{count} unmodified lines",
      binaryFile: "Binary file",
      untracked: "untracked",
      tooLarge: "Diff too large to display.",
      truncatedFile: "Diff truncated.",
      truncatedResult: "Some changes were omitted because the diff is very large.",
      statusAdded: "Added",
      statusDeleted: "Deleted",
      statusRenamed: "Renamed",
      statusModified: "Modified",
    },
    workspaceFiles: {
      label: "Thread workspace",
      expand: "Expand thread workspace",
      collapse: "Collapse thread workspace",
      showFiles: "Show thread files",
      dockRight: "Dock to the right",
      dockBottom: "Dock to the bottom",
      dragToDock: "Drag to dock right or bottom",
      workspace: "Thread",
      files: "Workspace",
      refresh: "Refresh thread workspace",
      loading: "Loading thread workspace…",
      empty: "No files touched in this thread yet",
      changed: "Changed",
      read: "Read",
      artifacts: "Artifacts",
      browser: "Project files",
      path: "Workspace path",
      root: "Root",
      search: "Search files",
      searchResults: "Search results",
      parentFolder: "Parent folder",
      noBrowserFiles: "No files in this folder.",
      noSearchResults: "No matching files.",
      truncated: "Showing the first matching files. Refine the search to narrow results.",
      session: "Thread",
      missing: "Missing",
      summary: "Thread workspace summary",
      changedCount: "{count} changed",
      readCount: "{count} read",
      artifactCount: "{count} artifacts",
      browserCount: "{count} shown",
      actions: "Workspace file actions",
      preview: "Preview",
      copyPath: "Copy path",
    },
  },
  languages: {
    en: "English",
    zhCN: "简体中文 (Simplified Chinese)",
    zhTW: "繁體中文 (Traditional Chinese)",
    ptBR: "Português (Brazilian Portuguese)",
    de: "Deutsch (German)",
    es: "Español (Spanish)",
    jaJP: "日本語 (Japanese)",
    ko: "한국어 (Korean)",
    fr: "Français (French)",
    hi: "हिन्दी (Hindi)",
    ar: "العربية (Arabic)",
    it: "Italiano (Italian)",
    tr: "Türkçe (Turkish)",
    uk: "Українська (Ukrainian)",
    id: "Bahasa Indonesia (Indonesian)",
    pl: "Polski (Polish)",
    th: "ไทย (Thai)",
    vi: "Tiếng Việt (Vietnamese)",
    nl: "Nederlands (Dutch)",
    fa: "فارسی (Persian)",
    ru: "Русский (Russian)",
  },
  cron: {
    tabs: {
      filterLabel: "Automation status",
      all: "All",
      active: "Active",
      paused: "Paused",
    },
    list: {
      viewLabel: "Automation views",
      searchPlaceholder: "Search automations",
      newTask: "New automation",
      filters: "Filters",
      shownOf: "{shown} of {total}",
      emptyTitle: "No automations yet",
      emptyHint: "Describe what OpenClaw should do and when — it runs on schedule.",
      noMatching: "No automations match the current filters.",
      loadMore: "Load more",
      loading: "Loading...",
      schedulerOff: "Scheduler disabled",
      refresh: "Refresh",
      refreshing: "Refreshing...",
      paused: "Paused",
      tasksTab: "Automations",
      activityTab: "Run history",
    },
    suggestions: {
      title: "Starter automations",
      add: "Add",
      schedules: {
        weekdayMornings: "Weekdays at 9:00 AM",
        everyMorning: "Daily at 8:00 AM",
        weekly: "Mondays at 9:00 AM",
        hourly: "Every hour",
      },
      ideas: {
        repoPulse: {
          name: "Repo pulse",
          tagline: "Overnight issues, PRs, and CI failures, ranked by urgency.",
          prompt:
            "Review overnight activity in my repositories: new issues, pull requests, and CI failures. Summarize the three things that most need my attention today, each with a link and a one-line reason.",
        },
        standupGhostwriter: {
          name: "Standup ghostwriter",
          tagline: "Your standup update, drafted from yesterday's work.",
          prompt:
            "Draft my standup update from yesterday's commits, merged pull requests, and open review threads. Three bullets max: done, doing, blocked.",
        },
        hackerNewsScout: {
          name: "Hacker News scout",
          tagline: "Three links worth your coffee, with hot takes.",
          prompt:
            "Scan today's Hacker News front page for posts about AI agents, developer tooling, and TypeScript. Send me the three most interesting links, each with a one-line hot take.",
        },
        dependencyRadar: {
          name: "Dependency radar",
          tagline: "Outdated or vulnerable dependencies, with upgrade notes.",
          prompt:
            "Check my main project for outdated or vulnerable dependencies. List the notable updates with a one-line risk note each, and draft the upgrade command.",
        },
        watchdog: {
          name: "Night watch",
          tagline: "Hourly health check with a one-line verdict.",
          prompt:
            "Check that my services and gateway are healthy: scan recent logs for new errors, restarts, or unusual load. Reply with a single short all-clear line when everything is fine; if something looks broken, report what failed and where to start looking.",
        },
        polyglotMinute: {
          name: "Polyglot minute",
          tagline: "One useful foreign phrase with your morning coffee.",
          prompt:
            "Teach me one useful phrase in Japanese: the phrase, how to pronounce it, its literal meaning, and when to use it. Keep it under five lines.",
        },
      },
    },
    stats: {
      tasks: "Automations",
      failing: "Failing",
      nextWake: "Next wake",
    },
    detail: {
      tabsLabel: "Automation details",
      newTitle: "New automation",
      newSubtitle: "Describe what OpenClaw should do, then pick when it runs.",
      back: "All automations",
      settingsTab: "Settings",
      historyTitle: "Run history",
      generalSection: "General",
      scheduleSection: "Schedule",
      deliverySection: "Delivery",
      active: "Active",
      paused: "Paused",
    },
    actions: {
      runNow: "Run now",
      runIfDue: "Run if due",
      pause: "Pause",
      resume: "Resume",
      clone: "Clone",
      remove: "Remove",
      more: "More actions",
    },
    runNotStarted: {
      notDue: "This automation is not due yet.",
      alreadyRunning: "This automation is already running.",
      recoveryPending: "Scheduler recovery is still in progress.",
      invalidSpec: "This automation has an invalid schedule or payload.",
      stopped: "The scheduler is stopped.",
      unknown: "This automation could not be started.",
    },
    jobs: {
      schedule: "Schedule",
      lastRun: "Last run",
      all: "All",
      sort: "Sort",
      nextRun: "Next run",
      recentlyUpdated: "Recently updated",
      name: "Name",
      direction: "Direction",
      ascending: "Ascending",
      descending: "Descending",
      reset: "Reset",
    },
    runs: {
      searchRuns: "Search runs",
      searchPlaceholder: "Summary, error, or task",
      newestFirst: "Newest first",
      oldestFirst: "Oldest first",
      status: "Status",
      delivery: "Delivery",
      clear: "Clear",
      allStatuses: "All statuses",
      allDelivery: "All delivery",
      noMatching: "No matching runs.",
      emptyTitle: "No runs yet",
      emptyHint: "Runs show up here once an automation fires.",
      loadMore: "Load more runs",
      runStatusOk: "OK",
      runStatusError: "Error",
      runStatusSkipped: "Skipped",
      runStatusUnknown: "Unknown",
      deliveryDelivered: "Delivered",
      deliveryNotDelivered: "Not delivered",
      deliveryUnknown: "Unknown",
      deliveryNotRequested: "Not requested",
    },
    form: {
      required: "Required",
      requiredSr: "required",
      fieldName: "Name",
      description: "Description",
      agentId: "Agent",
      namePlaceholder: "Morning brief",
      descriptionPlaceholder: "Optional context for this task",
      agentPlaceholder: "main or ops",
      agentHelp: "Start typing to pick a known agent, or enter a custom one.",
      promptPlaceholder: "Describe what OpenClaw should do...",
      repeat: "Repeat",
      repeatInterval: "Interval",
      repeatOnce: "Once",
      repeatOnExit: "On exit",
      onExitHelp: "Runs when a watched command exits. The schedule cannot be edited here.",
      every: "Every",
      at: "At",
      cronOption: "Cron",
      runAt: "Run at",
      unit: "Unit",
      minutes: "Minutes",
      hours: "Hours",
      days: "Days",
      expression: "Expression",
      expressionPlaceholder: "0 7 * * *",
      everyAmountPlaceholder: "30",
      summaryEverySecondOne: "Runs every second",
      summaryEveryMinuteOne: "Runs every minute",
      summaryEveryHourOne: "Runs every hour",
      summaryEveryDayOne: "Runs every day",
      summaryEverySeconds: "Runs every {amount} seconds",
      summaryEveryMinutes: "Runs every {amount} minutes",
      summaryEveryHours: "Runs every {amount} hours",
      summaryEveryDays: "Runs every {amount} days",
      summaryOnce: "Runs once at {at}",
      summaryCron: "Cron schedule {expr}",
      summaryCronTz: "Cron schedule {expr} ({tz})",
      timezoneOptional: "Timezone",
      timezonePlaceholder: "America/Los_Angeles",
      timezoneHelp: "Optional. Any valid IANA timezone.",
      runsIn: "Runs in",
      mainSession: "Main session",
      isolatedSession: "Isolated session",
      sessionHelp: "Main posts into the agent's timeline. Isolated runs a dedicated agent turn.",
      wakeMode: "Wake mode",
      now: "Now",
      nextHeartbeat: "Next heartbeat",
      wakeModeHelp: "Now triggers immediately. Next heartbeat waits for the next cycle.",
      action: "Action",
      command: "Command",
      systemEvent: "Post to main timeline",
      agentTurn: "Run agent task",
      systemEventHelp: "Sends your text to the gateway main timeline (good for reminders).",
      agentTurnHelp: "Starts an agent run in its own session using your prompt.",
      timeoutSeconds: "Timeout (seconds)",
      timeoutPlaceholder: "Optional, e.g. 90",
      timeoutHelp: "Optional. Leave blank to use the gateway default timeout for this run.",
      mainTimelineMessage: "Main timeline message",
      assistantTaskPrompt: "Prompt",
      deliveryModeLabel: "Mode",
      announceDefault: "Announce summary",
      webhookPost: "Webhook POST",
      noneInternal: "None (internal)",
      deliveryHelp: "Announce posts a summary to chat. None keeps execution internal.",
      webhookUrl: "Webhook URL",
      channel: "Channel",
      webhookPlaceholder: "https://example.com/cron",
      channelHelp: "Choose which connected channel receives the summary.",
      webhookHelp: "Send run summaries to a webhook endpoint.",
      to: "To",
      toPlaceholder: "+1555... or chat id",
      toHelp: "Optional recipient override (chat id, phone, or user id).",
      advanced: "Advanced",
      advancedHelp:
        "Optional overrides for delivery guarantees, schedule jitter, and model controls.",
      startEnabled: "Start enabled",
      deleteAfterRun: "Delete after run",
      deleteAfterRunHelp: "Best for one-shot reminders that should auto-clean up.",
      clearAgentOverride: "Clear agent override",
      clearAgentHelp: "Force this task to use the gateway default agent.",
      sessionKey: "Session key",
      sessionKeyHelp: "Optional routing key for job delivery and wake routing.",
      exactTiming: "Exact timing (no stagger)",
      exactTimingHelp: "Run on exact cron boundaries with no spread.",
      staggerWindow: "Stagger window",
      staggerUnit: "Stagger unit",
      staggerPlaceholder: "30",
      seconds: "Seconds",
      accountId: "Account ID",
      accountIdHelp: "Optional channel account ID for multi-account setups.",
      lightContext: "Light context",
      lightContextHelp: "Use lightweight bootstrap context for this agent job.",
      model: "Model",
      modelPlaceholder: "openai/gpt-5.2",
      modelHelp: "Start typing to pick a known model, or enter a custom one.",
      thinking: "Reasoning",
      thinkingPlaceholder: "low",
      thinkingHelp: "Use a suggested level or enter a provider-specific value.",
      failureAlerts: "Failure alerts",
      failureAlertInherit: "Inherit global setting",
      failureAlertDisabled: "Disable for this job",
      failureAlertCustom: "Custom per-job settings",
      failureAlertsHelp: "Control when this job sends repeated-failure alerts.",
      failureAlertAfter: "Alert after",
      failureAlertAfterHelp: "Consecutive errors before alerting.",
      failureAlertCooldown: "Cooldown (seconds)",
      failureAlertCooldownHelp: "Minimum seconds between alerts.",
      failureAlertChannel: "Alert channel",
      failureAlertTo: "Alert to",
      failureAlertToPlaceholder: "+1555... or chat id",
      failureAlertToHelp: "Optional recipient override for failure alerts.",
      failureAlertMode: "Alert mode",
      failureAlertAnnounce: "Announce (via channel)",
      failureAlertWebhook: "Webhook (HTTP POST)",
      failureAlertAccountId: "Alert account ID",
      failureAlertAccountPlaceholder: "Account ID for multi-account setups",
      bestEffortDelivery: "Best effort delivery",
      bestEffortHelp: "Do not fail the task if delivery itself fails.",
      cantAddYet: "Can't save yet",
      fillRequired: "Fill the required fields below to enable submit.",
      fixFields: "Fix {count} field to continue.",
      fixFieldsPlural: "Fix {count} fields to continue.",
      saving: "Saving...",
      saveChanges: "Save changes",
      createTask: "Create automation",
      createAndRun: "Create & run now",
      cancel: "Cancel",
    },
    jobState: {
      next: "Next",
    },
    runEntry: {
      noSummary: "No summary.",
      runAt: "Run at",
      openRunChat: "Open run chat",
      next: "Next {rel}",
      due: "Due {rel}",
    },
    errors: {
      nameRequired: "Name is required.",
      scheduleAtInvalid: "Enter a valid date/time.",
      everyAmountInvalid: "Interval must be greater than 0.",
      cronExprRequired: "Cron expression is required.",
      staggerAmountInvalid: "Stagger must be greater than 0.",
      systemTextRequired: "System text is required.",
      agentMessageRequired: "Agent message is required.",
      timeoutInvalid: "If set, timeout must be greater than 0 seconds.",
      webhookUrlRequired: "Webhook URL is required.",
      webhookUrlInvalid: "Webhook URL must start with http:// or https://.",
      invalidRunTime: "Invalid run time.",
      invalidIntervalAmount: "Invalid interval amount.",
      cronExprRequiredShort: "Cron expression required.",
      invalidStaggerAmount: "Invalid stagger amount.",
      systemEventTextRequired: "System event text required.",
      agentMessageRequiredShort: "Agent message required.",
      nameRequiredShort: "Name required.",
    },
  },
};
